import type { Locale } from "@/api/types";

export type MessageValue = string | { one: string; other: string };

const en = {
  "maintenance.title": "We are under maintenance",
  "maintenance.description": "Memoru is being updated and is not available for a short while. Please try again soon.",
  "maintenance.stillSignedIn": "You are still signed in. We will take you back to where you were when we are done.",
  "maintenance.retry": "Check again",
  "maintenance.checking": "Checking",
  "maintenance.autoCheck": "This page checks by itself, and takes you back when we are done.",
  "maintenance.readOnlyTitle": "Read-only for now.",
  "maintenance.readOnlyBody": "You can read your meetings, but changes are paused while we update Memoru.",
  "maintenance.tag": "Maintenance",
  "maintenance.language": "Language",
  "maintenance.game.invite": "Play a quick game while you wait.",
  "maintenance.game.label": "Echo pattern game",
  "maintenance.game.best": "Best",
  "maintenance.game.round": "Round",
  "maintenance.game.play": "Play",
  "maintenance.game.again": "Again",
  "maintenance.game.idle": "Repeat the pattern. How far can you go?",
  "maintenance.game.watch": "Watch the pattern",
  "maintenance.game.yourTurn": "Your turn",
  "maintenance.game.over": "Game over. Rounds cleared: {count}",
  "maintenance.game.how": "Tap the pads, or press 1 to 4 on a keyboard.",
  "maintenance.game.sound": "Sound",
  "maintenance.game.pad1": "Blue circle",
  "maintenance.game.pad2": "Purple square",
  "maintenance.game.pad3": "Green triangle",
  "maintenance.game.pad4": "Amber diamond",
  "app.account": "Account",
  "app.activity": "Activity",
  "app.availableLater": "Available in a later release",
  "app.commitments": "Commitments",
  "app.collapseSidebar": "Collapse sidebar",
  "app.customers": "Customers",
  "app.experimentalAccess": "Experimental access",
  "app.expandSidebar": "Expand sidebar",
  "app.extensionSetup": "Extension setup",
  "app.meetings": "Meetings",
  "app.spaces": "Spaces",
  "app.openCaptureSetup": "Open capture setup",
  "app.openNavigation": "Open navigation",
  "app.personalWorkspace": "Personal workspace",
  "app.workspace": "Workspace",
  "app.selectWorkspace": "Select workspace",
  "app.createWorkspace": "Create workspace",
  "app.createWorkspaceDescription": "Create a shared home for related meetings, people, and follow-up work.",
  "app.joinWorkspace": "Join a workspace",
  "app.workspaceActions": "Workspace actions",
  "app.workspaceName": "Workspace name",
  "app.noWorkspaces": "No workspaces",
  "app.sectionComingSoon": "This workspace section is connected to the shell and will become available as its server workflow is delivered.",
  "app.backToToday": "Back to Today",
  "onboarding.title": "Set up your workspace",
  "onboarding.subtitle": "Take a few minutes to understand how Memoru keeps meeting details useful and under your control.",
  "onboarding.step.language": "Language and processing",
  "onboarding.step.workspace": "Choose a workspace",
  "onboarding.step.extension": "Connect capture",
  "onboarding.language.title": "Choose your language",
  "onboarding.language.description": "This controls the Memoru interface. Meeting names, people, and quoted evidence stay in their original form.",
  "onboarding.processing.title": "Your data, clearly explained",
  "onboarding.processing.description": "Memoru saves permitted captions to your selected workspace and uses cloud AI only to prepare summaries. You can change AI processing and retention in Settings.",
  "onboarding.workspace.title": "Where should your work live?",
  "onboarding.workspace.description": "Personal meetings stay private. Team workspaces are shared only according to membership and each record's visibility.",
  "onboarding.workspace.current": "Current workspace",
  "onboarding.workspace.createHint": "Create a team workspace from the selector when you are ready to invite people.",
  "onboarding.extension.title": "Connect the capture extension",
  "onboarding.extension.description": "The web account is the single memoru sign-in. The extension captures captions locally and receives only a short-lived connection handoff, never your web refresh cookie.",
  "onboarding.extension.connected": "Your capture extension is connected.",
  "onboarding.extension.notConnected": "The extension is not connected yet. You can finish setup and connect it later.",
  "onboarding.extension.expired": "The extension pairing expired. Open extension setup to reconnect without restarting onboarding.",
  "onboarding.extension.captureRecovery": "A previous capture needs recovery. You can resume or restart it from the capture flow.",
  "onboarding.openExtensionSetup": "Open extension setup",
  "onboarding.back": "Back",
  "onboarding.next": "Continue",
  "onboarding.finish": "Finish setup",
  "onboarding.saving": "Saving setup progress",
  "onboarding.error": "Could not save setup progress. Try again.",
  "workspace.joinTitle": "Join a workspace",
  "workspace.joinDescription": "Enter the invitation code from your workspace owner or administrator.",
  "workspace.joinPrivacy": "Joining adds you to the team workspace only. Your personal meetings and history remain separate.",
  "workspace.inviteCode": "Invitation code",
  "workspace.previewInvite": "Preview invitation",
  "workspace.previewTitle": "Invitation details",
  "workspace.invitedBy": "Invited by",
  "workspace.assignedRole": "Assigned role",
  "workspace.defaultVisibility": "Default visibility",
  "workspace.previewExpires": "Expires",
  "workspace.previewRequired": "Preview the invitation before accepting it.",
  "workspace.previewError": "This invitation could not be previewed.",
  "workspace.acceptError": "This invitation could not be accepted.",
  "workspace.role.admin": "Administrator",
  "workspace.role.member": "Member",
  "workspace.role.billing_admin": "Billing administrator",
  "workspace.visibility.private": "Private by default",
  "workspace.visibility.workspace": "Shared with the workspace by default",
  "workspace.acceptInvite": "Accept invitation",
  "workspace.backToWorkspace": "Back to your workspace",
  "app.primaryNavigation": "Primary navigation",
  "app.searchPlaceholder": "Search anything in your workspace…",
  "app.searchWorkspace": "Search workspace",
  "app.settings": "Settings",
  "app.language": "Language",
  "app.interface": "Interface",
  "app.logOut": "Log out",
  "app.closeSidebar": "Close sidebar",
  "app.themeMenu": "Theme",
  "app.themeOptionLight": "Light",
  "app.themeOptionDark": "Dark",
  "app.themeOptionSystem": "System",
  "app.theme": "Appearance",
  "app.themeDark": "Dark mode",
  "app.themeLight": "Light mode",
  "app.themeSystem": "System mode",
  "settings.eyebrow": "Settings",
  "settings.title": "Make Memoru work your way.",
  "settings.subtitle": "Your workspace, tools, and boundaries.",
  "settings.navLabel": "Settings sections",
  "settings.nav.account": "Account",
  "settings.nav.connections": "Connections",
  "settings.nav.autonomy": "Agent permissions",
  "settings.nav.members": "Team members",
  "settings.nav.privacy": "Privacy & data",
  "settings.nav.billing": "Usage & billing",
  "settings.connectionsNotice": "Connecting a tool does not allow every action. Agent permissions still apply.",
  "account.loading": "Loading account",
  "account.profileTitle": "Account profile",
  "account.profileDescription": "Your signed-in identity and plan details.",
  "account.email": "Email",
  "account.plan": "Plan",
  "account.summaries": "Summaries this month",
  "account.experimental": "Experimental access is active. Usage is measured, but checkout, prices, invoices, renewals, and paid-plan limits are not shown.",
  "account.loginHistoryTitle": "Login history",
  "account.loginHistoryDescription": "Review the browser and extension sessions that still have access to your account.",
  "account.sessionsLoading": "Loading login history…",
  "account.sessionLoadError": "Could not load login history.",
  "account.sessionSignOutError": "Could not sign out that session.",
  "account.noSessions": "There are no active sessions to show.",
  "account.session.web": "Web app",
  "account.session.extension": "Memoru extension",
  "account.session.legacy": "Earlier sign-in",
  "account.signedInAt": "Signed in {date}",
  "account.lastActiveAt": "Last active {date}",
  "account.unknownDate": "an unknown time",
  "account.signOutSession": "Sign out",
  "account.revokeSessionTitle": "Sign out this session?",
  "account.revokeSessionDescription": "This signs {device} out on that device. It can sign in again when needed.",
  "account.signOutBrowserTitle": "Sign out from this browser",
  "account.signOutBrowserDescription": "End your current Memoru session on this browser. Other sessions remain signed in.",
  "account.signOutBrowser": "Sign out",
  "app.skipToContent": "Skip to main content",
  "app.signedIn": "Signed in",
  "app.signedInWorkspace": "Signed-in workspace",
  "app.today": "Today",
  "app.workspaceNavigation": "Workspace navigation",
  "app.workspaceOwner": "Workspace owner",
  "workspace.role.owner": "Workspace owner",
  "meetings.subtitle": "Live and past transcripts from this workspace.",
  "meetings.title": "Meetings",
  "meetings.directoryTitle": "Meetings Directory",
  "meetings.recordMeeting": "Record meeting",
  "meetings.recordMetadata": "Record Metadata",
  "meetings.searchLabel": "Search meetings",
  "meetings.searchPlaceholder": "Search meeting titles, space names, recaps...",
  "meetings.searching": "Searching…",
  "meetings.filters": "Filters",
  "meetings.filterDescription": "Narrow meetings by status, Space, platform, or date.",
  "meetings.activeFilters": "Active meeting filters",
  "meetings.clearFilters": "Clear filters",
  "meetings.clearSearch": "Clear search",
  "meetings.loading": "Loading meetings",
  "meetings.platform": "Platform",
  "meetings.dateRange": "Date range",
  "meetings.dateFrom": "From",
  "meetings.dateTo": "To",
  "meetings.pickDate": "Pick a date",
  "meetings.clearDate": "Clear date",
  "meetings.done": "Done",
  "meetings.fromChip": "From {date}",
  "meetings.toChip": "To {date}",
  "meetings.noSpaces": "No active Spaces",
  "meetings.sortBy": "Sort by:",
  "meetings.sortNewest": "Date (Newest)",
  "meetings.sortOldest": "Date (Oldest)",
  "meetings.sortTitle": "Title",
  "meetings.sortStatus": "Status",
  "meetings.statusProcessed": "Processed",
  "meetings.statusActionNeeded": "Action Needed",
  "meetings.statusChip": "Status: Action Needed",
  "meetings.columnTitle": "Meeting Title",
  "meetings.columnSpace": "Space Context",
  "meetings.columnWhen": "Date / Time",
  "meetings.spaceNone": "None",
  "meetings.showing": "Showing {from} to {to} of {total} entries",
  "meetings.previous": "Previous",
  "meetings.next": "Next",
  "meetings.statusLabel": "Status",
  "meetings.statusAll": "All meetings",
  "meetings.statusLive": "Live",
  "meetings.statusEnded": "Ended",
  "meetings.loadError": "Could not load meetings",
  "meetings.emptyTitle": "No meetings yet",
  "meetings.noMatch": "No matching meetings",
  "meetings.loadMore": "Load more meetings",
  "meetings.emptyDescription": "Start a capture or review the sample setup to give this workspace a useful starting point.",
  "meetings.noMatchDescription": "Try a different search or remove one of the active filters.",
  "meetings.openMatch": "Open match",
  "meetings.backToList": "All meetings",
  "spaces.title": "Spaces",
  "spaces.subtitle": "Organize related meetings and commitments without adding work to every meeting.",
  "spaces.create": "Create Space",
  "spaces.edit": "Edit Space",
  "spaces.createDescription": "Give this Space a clear name and optional context.",
  "spaces.editDescription": "Update the name or context. Existing links stay intact.",
  "spaces.search": "Search Spaces",
  "spaces.searchPlaceholder": "Search by name or description",
  "spaces.status": "Status",
  "spaces.active": "Active",
  "spaces.archived": "Archived",
  "spaces.all": "All",
  "spaces.sort": "Sort Spaces",
  "spaces.sortName": "Name",
  "spaces.sortUpdated": "Recently updated",
  "spaces.sortNewest": "Newest",
  "spaces.refreshing": "Updating Spaces…",
  "spaces.name": "Name",
  "spaces.description": "Description",
  "spaces.updated": "Updated",
  "spaces.actions": "Actions",
  "spaces.actionsFor": "Actions for {name}",
  "spaces.noDescription": "No description",
  "spaces.namePlaceholder": "e.g. Acme renewal",
  "spaces.descriptionPlaceholder": "Optional context for this work",
  "spaces.save": "Save changes",
  "spaces.archive": "Archive",
  "spaces.restore": "Restore",
  "spaces.emptyTitle": "No Spaces found",
  "spaces.emptySearch": "Try a different search or status.",
  "spaces.emptyDescription": "Create a Space to keep related meetings and commitments together.",
  "spaces.loadError": "Could not load Spaces.",
  "spaces.createError": "Could not create this Space.",
  "spaces.editError": "Could not update this Space. Refresh and try again.",
  "spaces.statusError": "Could not change this Space status. Refresh and try again.",
  "spaces.noWorkspace": "No workspace selected",
  "spaces.noWorkspaceDescription": "Select a workspace to manage Spaces.",
  "spaceDetail.allSpaces": "All Spaces",
  "spaceDetail.loadError": "Could not load this Space.",
  "spaceDetail.unavailable": "Space unavailable",
  "spaceDetail.noDescription": "No description has been added yet.",
  "spaceDetail.openCommitments": "Open commitments",
  "spaceDetail.noCommitments": "No open commitments",
  "spaceDetail.noCommitmentsDescription": "Confirmed follow-up work linked to this Space will appear here.",
  "spaceDetail.recentMeetings": "Recent meetings",
  "spaceDetail.noMeetings": "No meetings yet",
  "spaceDetail.noMeetingsDescription": "Meetings assigned to this Space will appear here.",
  "spaceDetail.metadata": "Space details",
  "spaceDetail.created": "Created",
  "spaceDetail.activity": "Recent activity",
  "spaceDetail.noActivity": "Edits, links, and status changes will appear here.",
  "today.handleFollowUp": "Handle a follow-up",
  "meeting.handleFollowUp": "Handle follow-up",
  "meeting.sectionsLabel": "Meeting sections",
  "meeting.tab.overview": "Overview",
  "meeting.tab.decisions": "Decisions",
  "meeting.tab.commitments": "Commitments",
  "meeting.tab.transcript": "Transcript",
  "meeting.tab.summary": "Summary",
  "meeting.tab.activity": "Activity",
  "meeting.tab.chat": "Ask AI",
  "chat.title": "Ask about this meeting",
  "chat.subtitle": "Answers come only from this meeting's transcript.",
  "chat.newChat": "New chat",
  "chat.chats": "Chats",
  "chat.chatsButton": "Chats ({count})",
  "chat.chatsSheetDescription": "Your earlier questions about this meeting.",
  "chat.noChats": "Your questions about this meeting will be listed here.",
  "chat.loadingChats": "Loading chats",
  "chat.chatsError": "Could not load your chats.",
  "chat.untitled": "New chat",
  "chat.messageCount": { one: "{count} message", other: "{count} messages" },
  "chat.deleteChat": "Delete chat",
  "chat.deleteTitle": "Delete this chat?",
  "chat.deleteBody": "“{title}” and its answers will be removed. This cannot be undone.",
  "chat.deleted": "Chat deleted",
  "chat.deleteError": "Could not delete the chat.",
  "chat.loadingMessages": "Loading the conversation",
  "chat.messagesError": "Could not load this conversation.",
  "chat.promptsTitle": "Try asking",
  "chat.emptyTitle": "What do you want to know?",
  "chat.emptyBody": "Ask about decisions, owners or deadlines. Every answer points to the moment it was said.",
  "chat.composerLabel": "Ask a question about this meeting",
  "chat.placeholder": "Ask about decisions, owners, deadlines…",
  "chat.send": "Send",
  "chat.stop": "Stop",
  "chat.counter": "{count} / {max}",
  "chat.hint": "Enter to send, Shift+Enter for a new line",
  "chat.questionTooLong": "Questions can be up to {max} characters.",
  "chat.busyHint": "Wait for the current answer to finish, or stop it.",
  "chat.you": "You",
  "chat.assistant": "Memoru AI",
  "chat.phase.sending": "Sending…",
  "chat.phase.reading": "Reading the transcript…",
  "chat.phase.writing": "Writing…",
  "chat.answerReady": "Answer ready",
  "chat.stopped": "Stopped. This answer may be incomplete.",
  "chat.lengthCut": "The answer was cut short because it reached the length limit.",
  "chat.stale": "The transcript changed after this answer was written.",
  "chat.unverified": "Not checked against the transcript. Verify before relying on it.",
  "chat.notInMeeting": "Not discussed in this meeting",
  "chat.claimSources": "Said by {sources}",
  "chat.speakerAt": "{speaker} at {time}",
  "chat.sourcesMenu": "Sources",
  "chat.sourcesTitle": "From the transcript",
  "chat.highlightToggle": "Highlight in the answer",
  "chat.viewInTranscript": "View in transcript",
  "chat.copy": "Copy answer",
  "chat.copied": "Copied",
  "chat.copyError": "Could not copy the answer.",
  "chat.helpful": "Helpful",
  "chat.notHelpful": "Not helpful",
  "chat.feedbackError": "Could not save your feedback.",
  "chat.regenerate": "Regenerate",
  "chat.jumpToLatest": "Latest",
  "chat.unavailable.live.title": "Ask AI opens when the meeting ends",
  "chat.unavailable.live.body": "The transcript is still being written. Come back once the meeting is over.",
  "chat.unavailable.noTranscript.title": "Nothing to ask about yet",
  "chat.unavailable.noTranscript.body": "This meeting has no transcript, so there is nothing to answer from.",
  "chat.unavailable.tooLong.title": "This meeting is too long to ask about",
  "chat.unavailable.tooLong.body": "The transcript is longer than the assistant can read in one go.",
  "chat.dailyLimit": "You have used all {limit} questions for today. You can ask again {when}.",
  "chat.usage": "{used} of {limit} questions used today",
  "chat.later": "later",
  "chat.chatFull": "This chat is full. Start a new chat to keep asking.",
  "chat.failure.rate_limited": "You are asking quickly. Try again in {seconds} seconds.",
  "chat.failure.rate_limited_soon": "You are asking quickly. Try again in a moment.",
  "chat.failure.daily_limit": "You have reached today's question limit. You can ask again {when}.",
  "chat.failure.busy": "Another answer is still being written. Wait for it to finish.",
  "chat.failure.full": "This chat is full. Start a new chat to keep asking.",
  "chat.failure.chat_limit": "This meeting has the most chats allowed. Delete one to start another.",
  "chat.failure.not_ready_live": "Questions open once the meeting ends.",
  "chat.failure.not_ready_no_transcript": "This meeting has no transcript to answer from.",
  "chat.failure.too_long": "This meeting is too long to ask about.",
  "chat.failure.validation": "That question could not be sent. Check it and try again.",
  "chat.failure.unavailable": "The AI service is unavailable right now. Try again in a moment.",
  "chat.failure.timeout": "The answer took too long. Try again.",
  "chat.failure.not_found": "This chat is no longer available.",
  "chat.failure.unauthorized": "Your session has ended. Sign in again.",
  "chat.failure.network": "The connection was lost. Check your network and try again.",
  "chat.failure.unknown": "Something went wrong. Try again.",
  "meeting.loading": "Loading meeting",
  "meeting.unavailable": "Meeting unavailable",
  "meeting.notFound": "This meeting could not be found.",
  "meeting.noSpace": "No Space",
  "meeting.moreActions": "More meeting actions",
  "meeting.share": "Share",
  "meeting.shareTitle": "Share reviewed meeting",
  "meeting.shareDescription": "Choose exactly what a named workspace member can access. Recap-only access never exposes the transcript.",
  "meeting.shareError": "Could not share this meeting.",
  "meeting.recipientLabel": "Recipient user ID",
  "meeting.recipientPlaceholder": "Workspace member ID",
  "meeting.shareInclude": "Include",
  "meeting.shareRecapOnly": "Reviewed recap only",
  "meeting.shareFullTranscript": "Recap and full transcript",
  "meeting.titleLabel": "Meeting title",
  "meeting.renameDescription": "Use a clear title that will be easy to find later.",
  "meetingSpaces.title": "Space assignment",
  "meetingSpaces.description": "Choose the active context this meeting belongs to. Meetings can also remain without a Space.",
  "meetingSpaces.empty": "No Spaces are available yet.",
  "meetingSpaces.save": "Save Spaces",
  "meetingSpaces.saved": "Space assignment saved.",
  "meetingSpaces.loadError": "Could not load Spaces.",
  "meetingSpaces.saveError": "Could not save Space assignment.",
  "meetingReport.insights": "Meeting insights",
  "meetingReport.insightsEmpty": "Decisions, commitments, and unresolved questions will appear after a summary is generated.",
  "meetingReport.summaryLabel": "Meeting summary",
  "meetingReport.overviewLabel": "Meeting overview",
  "meetingReport.narrativeTitle": "The conversation, in brief",
  "meetingReport.narrativeDescription": "A concise narrative recap grounded in the transcript.",
  "meetingReport.decisionsTitle": "What was decided",
  "meetingReport.decisionCount": { one: "{count} decision", other: "{count} decisions" },
  "meetingReport.grounded": "Grounded in the reviewed meeting source.",
  "meetingReport.noDecisions": "No decisions recorded.",
  "meetingReport.commitmentsTitle": "Commitments to carry forward",
  "meetingReport.noCommitments": "No commitments recorded.",
  "meetingReport.prepared": "Prepared from source",
  "meetingReport.unresolvedTitle": "Still to resolve",
  "meetingReport.noQuestions": "No open questions recorded.",
  "meeting.activityTitle": "Meeting activity",
  "meeting.activityDescription": "Lifecycle events for this meeting. Workflow receipts and recovery appear when a follow-through run exists.",
  "meeting.activityStarted": "Meeting started",
  "meeting.activityEnded": "Meeting ended",
  "meeting.pauseLiveFollowing": "Pause live following",
  "meeting.followLiveTranscript": "Follow latest transcript",
  "summary.optionsDescription": "Choose the language and shape for this summary. Names and source quotes stay unchanged.",
  "summary.outputLanguage": "Output language",
  "summary.language.en": "English",
  "summary.language.id": "Bahasa Indonesia",
  "summary.language.mixed": "Mixed",
  "summary.template": "Summary template",
  "summary.template.general": "General recap",
  "summary.template.customerCall": "Customer call",
  "summary.template.customerSuccess": "Customer success",
  "summary.regenerateReport": "Regenerate report",
  "summary.chat.userRequest": "User request",
  "summary.chat.assistant": "Memoru AI",
  "summary.chat.grounded": "Grounded",
  "summary.chat.sourceCitations": "Source citations",
  "summary.chat.starterTitle": "Grounded starter prompts",
  "summary.chat.prompt.summary": "Detailed summary",
  "summary.chat.prompt.decisions": "Explain decisions",
  "summary.chat.prompt.nextSteps": "Review next steps",
  "summary.chat.request.summary": "Give me a detailed summary of this meeting.",
  "summary.chat.request.decisions": "What decisions were made in this meeting?",
  "summary.chat.request.nextSteps": "What are the agreed next steps?",
  "summary.chat.composerPlaceholder": "Ask AI about this meeting…",
  "summary.chat.ask": "Ask",
  "summary.chat.unavailable": "Open-ended meeting questions need server support and are not available yet.",
  "summary.chat.reportTools": "Report tools",
  "summary.chat.reportMeta": "Report v{version} · {status} · {language}",
  "summary.chat.compareFrom": "Compare from report version",
  "summary.chat.compare": "Compare",
  "summary.chat.approve": "Approve",
  "summary.chat.editSummary": "Edit summary",
  "summary.chat.reviewedRecap": "Reviewed recap",
  "summary.chat.saveCorrection": "Save correction",
  "summary.chat.changesFrom": "Changes from version {version}",
  "summary.chat.noChanges": "No material changes.",
  "meeting.staleReport": "Transcript changed since this report",
  "meeting.staleReportDescription": "Review the evidence and update the report before using it for follow-through.",
  "meeting.reviewStale": "Review report",
  "customers.subtitle": "Keep customer context linked to the workspace, with explicit associations.",
  "customers.searchLabel": "Search customers",
  "customers.searchPlaceholder": "Search by name or domain",
  "customers.createTitle": "Add a customer",
  "customers.name": "Customer name",
  "customers.namePlaceholder": "Acme, Inc.",
  "customers.domain": "Domain (optional)",
  "customers.create": "Add customer",
  "customers.loading": "Loading customers",
  "customers.loadError": "Could not load customer",
  "customers.createError": "Could not create customer",
  "customers.updateError": "Could not update customer",
  "customers.editTitle": "Edit customer",
  "customers.edit": "Edit customer",
  "customers.cancelEdit": "Cancel editing",
  "customers.save": "Save customer",
  "customers.historyTitle": "Customer history",
  "customers.noHistory": "No customer history yet.",
  "customers.emptyTitle": "No customers yet",
  "customers.noMatch": "No matching customers",
  "customers.emptyDescription": "Create a customer to keep meeting context explicit and reviewable.",
  "customers.backToList": "Back to customers",
  "customers.noDomain": "No domain recorded",
  "customers.meetingsTitle": "Linked meetings",
  "customers.noMeetings": "No meetings are linked to this customer yet.",
  "customers.commitmentsTitle": "Open commitments",
  "customers.noCommitments": "No commitments are linked to this customer yet.",
  "customers.findDuplicates": "Check for duplicates",
  "customers.mergeTitle": "Review customer merge",
  "customers.closeMerge": "Close merge review",
  "customers.mergeWarning": "Similar customers are never merged automatically. Review the selected source, target, and conflicts before confirming.",
  "customers.loadingMergeCandidates": "Loading possible duplicates",
  "customers.mergeTarget": "Target customer",
  "customers.chooseCustomer": "Choose a customer",
  "customers.noMergeCandidates": "No possible duplicate customers were found.",
  "customers.previewMerge": "Preview merge",
  "customers.mergePreview": "Merge preview",
  "customers.noMergeConflicts": "No conflicts were found. The merge still requires explicit confirmation.",
  "customers.confirmMerge": "Confirm merge",
  "customers.mergeError": "Could not review or confirm customer merge",
  "followThrough.title": "Follow through",
  "followThrough.description": "Associations and commitments stay explicit, reviewable, and separate from the meeting recap.",
  "followThrough.customerTitle": "Customer association",
  "followThrough.customerLabel": "Customer",
  "followThrough.noCustomer": "No customer selected",
  "followThrough.confirmAssociation": "Confirm association",
  "followThrough.associated": "Association saved",
  "followThrough.commitmentTitle": "Commitment",
  "followThrough.textLabel": "Commitment text",
  "followThrough.textPlaceholder": "Describe the next action",
  "followThrough.dueLabel": "Due phrase (optional)",
  "followThrough.duePlaceholder": "before Friday",
  "followThrough.suggest": "Save as suggestion",
  "followThrough.suggested": "Suggested",
  "followThrough.confirm": "Confirm commitment",
  "followThrough.confirmed": "Confirmed",
  "followThrough.complete": "Mark complete",
  "followThrough.reopen": "Reopen",
  "followThrough.noDueDate": "No due date",
  "followThrough.shareTitle": "Share reviewed recap",
  "followThrough.shareDescription": "This grants recap access only. It does not expose transcript or recording access.",
  "followThrough.recipientLabel": "Recipient user ID",
  "followThrough.recipientPlaceholder": "Workspace member ID",
  "followThrough.noRecipient": "Select a workspace member",
  "followThrough.audienceLabel": "Access to share",
  "followThrough.audience.recap": "Reviewed recap only",
  "followThrough.audience.transcript": "Reviewed recap and transcript",
  "followThrough.audience.recapDescription": "The recipient can read the reviewed structured recap, but not the transcript or recording.",
  "followThrough.audience.transcriptDescription": "The recipient can read the reviewed recap and full transcript. Recording access stays separate.",
  "followThrough.shareRecap": "Share recap only",
  "followThrough.shareTranscript": "Share recap and transcript",
  "followThrough.sharedRecap": "Recap shared with",
  "followThrough.associationError": "Could not save the customer association",
  "followThrough.removeAssociation": "Remove association",
  "followThrough.removeAssociationError": "Could not remove the customer association",
  "followThrough.suggestionError": "Could not save the commitment suggestion",
  "followThrough.confirmationError": "Could not confirm the commitment",
  "followThrough.statusError": "Could not update the commitment",
  "followThrough.shareError": "Could not share the recap",
  "followThrough.revokeError": "Could not revoke recap access",
  "followThrough.unknownRecipient": "Unknown member",
  "followThrough.revoked": "Revoked",
  "followThrough.revoke": "Revoke access",
  "commitments.subtitle": "Track confirmed commitments with status, ownership, due context, and history.",
  "commitments.loading": "Loading commitments",
  "commitments.loadError": "Could not load commitments",
  "commitments.statusError": "Could not update commitment",
  "commitments.historyError": "Could not load commitment history",
  "commitments.statusLabel": "Show commitments",
  "commitments.status.open": "Open",
  "commitments.status.completed": "Completed",
  "commitments.status.cancelled": "Cancelled",
  "commitments.status.all": "All",
  "commitments.emptyTitle": "No commitments",
  "commitments.emptyDescription": "Confirmed commitments from reviewed meetings will appear here.",
  "commitments.openMeeting": "Open source meeting",
  "commitments.owner": "Owner",
  "commitments.customer": "Customer",
  "commitments.noCustomer": "No customer",
  "commitments.unassigned": "Unassigned",
  "commitments.noDate": "No date",
  "commitments.complete": "Mark complete",
  "commitments.reopen": "Reopen",
  "commitments.showHistory": "Show history",
  "commitments.hideHistory": "Hide history",
  "commitments.edit": "Edit commitment",
  "commitments.editTitle": "Edit commitment",
  "commitments.closeEditor": "Close commitment editor",
  "commitments.editError": "Could not edit commitment",
  "commitments.text": "Commitment",
  "commitments.duePhrase": "Original due phrase",
  "commitments.dueDate": "Due date",
  "commitments.timezone": "Timezone",
  "commitments.taskLinks": "External task links",
  "commitments.taskProvider": "Task provider",
  "commitments.taskId": "External task ID",
  "commitments.taskUrl": "Task URL (optional)",
  "commitments.addTask": "Add task link",
  "commitments.taskError": "Could not update external task link",
  "commitments.save": "Save commitment",
  "activity.subtitle": "Authoritative workflow runs, receipts, failures, and recovery actions.",
  "activity.loading": "Loading activity",
  "activity.loadError": "Could not load activity",
  "activity.retryError": "Could not retry remaining work",
  "activity.runsTitle": "Workflow runs",
  "activity.noRuns": "No workflow runs yet.",
  "activity.timelineTitle": "Activity timeline",
  "activity.noActivity": "No workflow activity yet.",
  "activity.reconciliationRequired": "Reconciliation required",
  "activity.retry": "Retry remaining ({count})",
  "activity.receipts": "Receipts",
  "activity.verified": "verified",
  "activity.failed": "failed",
  "activity.reconciling": "reconciling",
  "activity.recorded": "recorded",
  "activity.createGoal": "Create a reviewed goal",
  "autonomy.title": "Workspace policy",
  "autonomy.subtitle": "Manage the narrow workspace controls evaluated by the server.",
  "autonomy.loading": "Loading workspace policy",
  "autonomy.loadError": "Could not load workspace policy",
  "autonomy.unavailable": "Workspace policy is unavailable.",
  "autonomy.updateError": "Could not update workspace policy",
  "autonomy.backToSettings": "Back to settings",
  "autonomy.visibilityTitle": "Default visibility",
  "autonomy.visibilityDescription": "Choose the server-enforced default for new workspace content.",
  "autonomy.crmTitle": "CRM notes and tasks",
  "autonomy.crmDescription": "Allow approved workflows to create CRM notes and tasks when the server policy permits it.",
  "autonomy.crmLabel": "Allow CRM notes and tasks",
  "autonomy.emailLabel": "Email sending",
  "autonomy.reviewRequired": "Review required",
  "autonomy.adminOnly": "Only workspace owners and admins can change policy.",
  "autonomy.saving": "Saving policy",
  "goal.title": "Goal review",
  "goal.loading": "Loading goal review",
  "goal.loadError": "Could not load goal review",
  "goal.unavailable": "This goal review is unavailable.",
  "goal.mutationError": "Could not update action review",
  "goal.backToActivity": "Back to activity",
  "goal.policyVersion": "Policy version",
  "goal.stepsTitle": "Selectable steps",
  "goal.edit": "Review step",
  "goal.selected": "Selected for approval",
  "goal.manual": "Manual or excluded",
  "goal.evidence": "Evidence",
  "goal.editTitle": "Edit action",
  "goal.reason": "Reason",
  "goal.payload": "Exact action payload",
  "goal.saveDraft": "Save revised step",
  "goal.keepManual": "Keep manual",
  "goal.reject": "Reject step",
  "goal.createTitle": "Create a reviewed goal",
  "goal.createDescription": "Provide the server provenance and exact first action. The server will evaluate policy before execution.",
  "goal.createError": "Could not create goal",
  "goal.titleLabel": "Goal title",
  "goal.objective": "Objective",
  "goal.provenanceTitle": "Source provenance",
  "goal.sourceVersion": "Source version",
  "goal.sourceDigest": "Source digest",
  "goal.stepTitle": "First reviewable step",
  "goal.kind": "Action kind",
  "goal.create": "Create goal for review",
  "billing.usageTitle": "Current usage budget",
  "billing.usageUnits": "units used",
  "billing.usageRemaining": "remaining",
  "billing.usageReserved": "Reserved",
  "billing.actionsTitle": "Billing actions",
  "billing.checkout": "Open checkout",
  "billing.portal": "Open billing portal",
  "billing.actionUnavailable": "This billing action is unavailable.",
  "workflow.retryError": "Could not retry remaining work",
  "workflow.recoveryTitle": "Recover remaining work",
  "workflow.recoveryDescription": "Only failed work with unchanged, still-valid approval can be queued. Successful receipts remain visible.",
  "workflow.retryRemaining": "Retry remaining",
  "workflow.reconciliationTitle": "Reconciliation",
  "workflow.reconciliationDescription": "The provider outcome is uncertain. Do not resend until reconciliation resolves it.",
  "workflow.reconciliationClear": "No unresolved provider outcomes remain.",
  "workflow.title": "Workflow run",
  "workflow.subtitle": "Review authoritative actions, effects, receipts, and recovery state.",
  "workflow.loading": "Loading workflow run",
  "workflow.loadError": "Could not load workflow run",
  "workflow.unavailable": "This workflow run is unavailable.",
  "workflow.updateError": "Could not update workflow run",
  "workflow.backToActivity": "Back to activity",
  "workflow.state": "State",
  "workflow.pause": "Pause run",
  "workflow.resume": "Resume run",
  "workflow.cancel": "Cancel future work",
  "workflow.approvalUnavailable": "This run needs review, but the server response does not include the policy version required to bind an approval. No approval control is enabled.",
  "workflow.approvalError": "Could not approve this exact action",
  "workflow.policySummary": "Server policy",
  "workflow.emailReviewRequired": "email send requires review",
  "workflow.emailReviewOptional": "email send review is not required",
  "workflow.maxMutations": "maximum external mutations",
  "workflow.approve": "Approve exact action",
  "workflow.actionsTitle": "Actions",
  "workflow.requiresApproval": "approval required",
  "workflow.noApproval": "no approval required",
  "workflow.sourceVersion": "Source version",
  "workflow.payloadHash": "Payload hash",
  "workflow.receiptsTitle": "Receipts",
  "workflow.noReceipts": "No receipts yet.",
  "workflow.activityTitle": "Activity",
  "workflow.recorded": "recorded",
  "billing.title": "Billing status",
  "billing.subtitle": "View the server-authoritative commercial state for this workspace.",
  "billing.loading": "Loading billing status",
  "billing.loadError": "Could not load billing status",
  "billing.unavailable": "Billing status unavailable",
  "billing.unavailableDescription": "Billing status is not available for this workspace.",
  "billing.backToSettings": "Back to settings",
  "billing.accessTitle": "Commercial access",
  "billing.mode": "Mode",
  "billing.state": "State",
  "billing.entitled": "Commercial features are currently entitled.",
  "billing.notEntitled": "Commercial features are not currently entitled.",
  "billing.mode.bypass": "Experimental access",
  "billing.mode.shadow": "Shadow evaluation",
  "billing.mode.enforced": "Enforced billing",
  "billing.state.no_subscription": "No subscription",
  "billing.state.trialing": "Trialing",
  "billing.state.active": "Active",
  "billing.state.grace": "Grace period",
  "billing.state.payment_failed": "Payment failed",
  "billing.state.cancellation_scheduled": "Cancellation scheduled",
  "billing.state.paused": "Paused",
  "billing.state.unpaid": "Unpaid",
  "billing.state.cancelled": "Cancelled",
  "billing.state.expired": "Expired",
  "billing.state.incomplete": "Incomplete",
  "billing.state.incomplete_expired": "Incomplete and expired",
  "billing.subscriptionTitle": "Subscription",
  "billing.plan": "Plan",
  "billing.status": "Status",
  "billing.amount": "Amount",
  "billing.interval": "Billing interval",
  "billing.seats": "Seats",
  "billing.periodEnd": "Current period ends",
  "billing.noDate": "No date",
  "billing.cancellationScheduled": "Cancellation is scheduled for the end of the current period.",
  "billing.renewalExpected": "Renewal is currently expected according to the server state.",
  "billing.noSubscriptionTitle": "No subscription",
  "billing.noSubscriptionDescription": "This workspace does not currently have a subscription record.",
  "billing.actionsPending": "Checkout, portal, invoices, and usage controls remain unavailable until their server contracts are ready.",
  "members.title": "Workspace members",
  "members.subtitle": "Review active membership and refresh roles without another login.",
  "members.loading": "Loading workspace members",
  "members.loadError": "Could not load workspace members",
  "members.roleError": "Could not update member role",
  "members.revokeError": "Could not revoke workspace membership",
  "members.revokeConfirm": "Revoke this member's workspace access?",
  "members.backToSettings": "Back to settings",
  "members.owner": "Workspace owner",
  "members.role": "Role",
  "members.admin": "Administrator",
  "members.member": "Member",
  "members.billingAdmin": "Billing administrator",
  "members.revoke": "Revoke access",
  "members.readOnly": "Read-only",
  "members.defaultVisibility": "Default visibility",
  "privacy.title": "Privacy controls",
  "privacy.subtitle": "Control AI processing, retention, export, and deletion for your account.",
  "privacy.loading": "Loading privacy controls",
  "privacy.loadError": "Could not load privacy controls",
  "privacy.processingTitle": "AI processing and retention",
  "privacy.processingDescription": "Turning AI processing off prevents new AI summaries. Existing retained records are not silently deleted.",
  "privacy.processingLabel": "Allow cloud AI to prepare meeting summaries",
  "privacy.retentionLabel": "Retain records for (days)",
  "privacy.retentionError": "Retention must be between 1 and 3650 days.",
  "privacy.save": "Save privacy settings",
  "privacy.saveError": "Could not save privacy settings",
  "privacy.exportTitle": "Export your data",
  "privacy.exportDescription": "Download a no-store JSON export of your profile, privacy settings, meetings, transcript evidence, and reports.",
  "privacy.export": "Download privacy export",
  "privacy.exportError": "Could not export your data",
  "privacy.deleteTitle": "Delete account",
  "privacy.deleteDescription": "Deletion removes your usable account content and queues cleanup work. External copies, backups, and other users’ records are outside this operation.",
  "privacy.deleteConfirmation": "Type DELETE MY ACCOUNT to confirm",
  "privacy.delete": "Delete account",
  "privacy.deleteError": "Could not delete your account",
  "privacy.deletedTitle": "Account deleted",
  "privacy.deletedDescription": "Your account content was removed from usable views. Continue to finish signing out.",
  "privacy.deletionDetailsTitle": "Deletion status",
  "privacy.deletionScope": "Deleted scope",
  "privacy.deletionPending": "Pending cleanup tasks",
  "privacy.deletionExternalCopies": "External copies",
  "privacy.externalCopiesNotTracked": "Not tracked by Memoru",
  "privacy.externalCopiesReconciliation": "Reconciliation required",
  "privacy.deletionBackupExpiry": "Backup expiry",
  "privacy.deletionDevicePurge": "Device purge",
  "privacy.continueAfterDeletion": "Continue",
  "privacy.backToSettings": "Back to settings",
  "connections.title": "Business connections",
  "connections.subtitle": "Connect approved business tools for workspace follow-through.",
  "connections.loading": "Loading business connections",
  "connections.loadError": "Could not load business connections",
  "connections.connectError": "Could not start the connection",
  "connections.healthError": "Could not check connector health",
  "connections.disconnectError": "Could not disconnect this connector",
  "connections.backToSettings": "Back to settings",
  "connections.availableTitle": "Connected to your work",
  "connections.availableDescription": "Choose where Memoru can read context and complete actions.",
  "connections.connectGmail": "Connect Gmail",
  "connections.connectHubspot": "Connect HubSpot",
  "connections.emptyTitle": "No business connections",
  "connections.emptyDescription": "Connect Gmail or HubSpot when you are ready to use workspace follow-through.",
  "connections.accountType": "Account type",
  "connections.apiVersion": "API version",
  "connections.health": "Health",
  "connections.scopes": "Granted scopes",
  "connections.refreshHealth": "Check health",
  "connections.disconnect": "Disconnect",
  "connections.disconnectConfirm": "Disconnect this business connector?",
  "connections.status.active": "Active",
  "connections.status.revoked": "Revoked",
  "connections.status.expired": "Expired",
  "connections.status.reconciling": "Reconciling",
  "connections.health.unknown": "Unknown",
  "connections.health.healthy": "Healthy",
  "connections.health.unhealthy": "Unhealthy",
  "connections.health.reconciling": "Reconciling",
  "today.title": "Today",
  "today.subtitle": "A focused view of work that needs attention in this workspace.",
  "today.needsReview": "Needs review",
  "today.blocked": "Blocked",
  "today.due": "Due soon",
  "today.recentVerified": "Recently verified",
  "today.followUp": "Handle follow-up",
  "today.sectionEmpty": "Nothing in this section.",
  "today.emptyTitle": "Nothing needs attention yet",
  "today.emptyDescription": "Start a capture or review the sample setup to give this workspace a useful starting point.",
  "today.noWorkspace": "Choose a workspace",
  "today.noWorkspaceDescription": "Select an active workspace before viewing Today.",
  "today.loadError": "Could not load Today",
  "today.reason.deadline": "Deadline",
  "today.reason.dependency": "Dependency",
  "today.reason.recency": "Recent",
  "today.resource.meeting": "Meeting",
  "today.resource.customer": "Customer",
  "today.resource.space": "Space",
  "today.resource.commitment": "Commitment",
  "today.resource.activity": "Activity",
  "today.resource.goal": "Goal",
  "search.title": "Search",
  "search.subtitle": "Results for “{query}”",
  "search.emptyTitle": "Search your workspace",
  "search.emptyDescription": "Search meetings, customers, commitments, and exact evidence segments.",
  "search.dialogTitle": "Workspace search",
  "search.clear": "Clear search",
  "search.searching": "Searching your workspace…",
  "search.noResults": "No results",
  "search.noResultsDescription": "Try a different phrase or check another workspace.",
  "search.loadError": "Could not search this workspace",
  "search.kind.customer": "Customer",
  "search.kind.space": "Space",
  "search.kind.meeting": "Meeting",
  "search.kind.commitment": "Commitment",
  "search.kind.segment": "Evidence",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.create": "Create",
  "common.language": "Language",
  "common.loading": "Loading",
  "common.retry": "Try again",
  "error.goBack": "Go back",
  "error.goHome": "Go to Today",
  "error.notFound.description": "The page may have moved, been deleted, or the address may be incorrect.",
  "error.notFound.title": "Page not found",
  "error.page.description": "An unexpected error stopped this page from loading. Try again or return to Today.",
  "error.page.title": "This page could not load",
  "notifications.dismiss": "Dismiss notification",
  "notifications.label": "Notifications",
  "locale.en": "English",
  "locale.id": "Bahasa Indonesia",
  "meeting.sourceLabel": "Source: {source}",
  "meeting.rename": "Rename meeting",
  "meeting.renameSave": "Save title",
  "meeting.renameCancel": "Cancel",
  "meeting.renameError": "Could not rename meeting",
  "meeting.delete": "Delete meeting",
  "meeting.deleteShort": "Delete",
  "meeting.deleteDialogTitle": "Delete this meeting?",
  "meeting.deleteWarningTitle": "This action is permanent",
  "meeting.deleteDescription": "“{title}” will be removed from your workspace and cannot be recovered.",
  "meeting.deleteScopeTitle": "The following will be removed",
  "meeting.deleteScopeMeeting": "Meeting details and title",
  "meeting.deleteScopeTranscript": "Transcript, recap, and generated reports",
  "meeting.deleteScopeEvidence": "Saved evidence and processing status",
  "meeting.deleteConfirmationLabel": "Type “Delete” to confirm",
  "meeting.deleteConfirmationHint": "This extra step helps prevent accidental deletion.",
  "meeting.deleteConfirm": "Delete permanently",
  "meeting.deleteCancel": "Keep meeting",
  "meeting.deleteError": "Could not delete meeting. Your meeting is still safe. Try again.",
  "meeting.transcriptOriginal": "Original transcript",
  "meeting.transcriptDescription": "Caption text · no audio or video recording",
  "meeting.transcriptUpdated": "Updated {time}",
  "meeting.transcriptWaiting": "Waiting for transcript segments…",
  "meeting.transcriptLoadingMore": "Loading more transcript…",
  "meeting.metadata.title": "Meeting metadata",
  "meeting.metadata.meetingType": "Meeting type",
  "meeting.metadata.videoSync": "Video sync",
  "meeting.metadata.date": "Date",
  "meeting.metadata.duration": "Duration",
  "meeting.metadata.durationMinutes": { one: "{count} minute", other: "{count} minutes" },
  "meeting.metadata.platform": "Platform",
  "meeting.metadata.participants": "Participants",
  "meeting.metadata.participantCount": { one: "{count} participant", other: "{count} participants" },
  "meeting.activityViewDetails": "View details",
  "meeting.activityDetailsTitle": "Activity details",
  "meeting.activityDetailsSource": "Meeting",
  "meeting.activityDetailsTime": "Time",
  "meeting.segmentCount": { one: "{count} segment", other: "{count} segments" },
  "state.empty.title": "Nothing here yet",
  "state.error.title": "Something went wrong",
  "state.offline.description": "Check your connection. Your last available information is still shown.",
  "state.offline.title": "You are offline",
  "state.stale.description": "This information may have changed. Refresh before taking action.",
  "state.stale.title": "Update available",
  "welcome.eyebrow": "Welcome",
  "welcome.title": "Your meetings, remembered.",
  "welcome.intro": "Grant access to the meeting sites you use, then sign in once to keep your captions, transcripts, and notes together.",
  "welcome.point1": "Capture live captions from Google Meet and Zoom Web.",
  "welcome.point2": "Review searchable transcripts and summaries in Memoru.",
  "welcome.point3": "Memoru captures caption text only — never meeting audio.",
  "welcome.setupTitle": "Set up the extension",
  "welcome.setupDescription": "Chrome will ask you to confirm access before the extension can run on a meeting page.",
  "welcome.continue": "Continue to grant access",
  "welcome.missingDetails": "Extension setup details are missing. Open this page from the Memoru extension and try again.",
  "welcome.openError": "Could not open extension setup. Try again.",
  "welcome.accessNote": "You can change site access later in the extension settings.",
  "connect.title": "Connecting your extension",
  "connect.description": "Confirming your existing Memoru web session with Chrome. Your web session cookie is never shared with the extension.",
  "connect.missingDetails": "Extension setup details are missing. Return to the extension and start sign-in again.",
  "connect.connecting": "Connecting the extension",
  "connect.error": "Could not connect the extension. Try again.",
  "auth.checkingSession": "Checking session",
  "auth.checkingSetup": "Checking setup",
  "login.invalidLink": "This sign-in link is invalid or expired. Request a new magic link.",
  "login.enterEmailReceived": "Enter the email address that received the magic link.",
  "login.completeFailed": "We couldn't complete sign-in. Request a new magic link or try again in a moment.",
  "login.googleFailed": "Google sign-in failed. Try again.",
  "login.invalidEmail": "Enter a valid email address.",
  "login.sendFailed": "Could not send a sign-in email right now. Try again later.",
  "login.completeTitle": "Complete sign in",
  "login.checkInbox": "Check your inbox",
  "login.title": "Sign in to Memoru",
  "login.confirmEmail": "Confirm the email address that received your magic link.",
  "login.sentTo": "We sent a magic link to {email}. Open it to continue.",
  "login.tagline": "Your meeting notes, captured automatically from Google Meet and Zoom.",
  "login.linkExpires": "The link expires in 15 minutes. You can close this window after opening it.",
  "login.useAnother": "Use another email",
  "login.emailAddress": "Email address",
  "login.continue": "Continue",
  "login.sendLink": "Send magic link",
  "login.back": "Back",
  "login.google": "Continue with Google",
  "login.or": "or",
  "login.email": "Continue with email",
  "login.unavailable": "Sign-in is temporarily unavailable. Try again later.",
  "login.agreePrefix": "By continuing you agree to Memoru's ",
  "login.terms": "Terms of Service",
  "login.and": " and ",
  "login.privacy": "Privacy Policy",
  "login.agreeSuffix": ".",
  "login.err.rateLimit": "Too many sign-in attempts. Try again later.",
  "login.err.network": "Could not reach the sign-in service. Check your connection and try again.",
  "login.err.cancelled": "Google sign-in was cancelled.",
  "login.err.popupBlocked": "Allow pop-ups to continue with Google.",
  "setup.eyebrow": "Memoru setup",
  "setup.checking": "Checking your setup",
  "setup.nameRequired": "Enter a workspace name.",
  "setup.createFailed": "Could not create your workspace. Try again.",
  "setup.finishFailed": "Could not finish setup. Try again.",
  "setup.createTitle": "Create your workspace",
  "setup.createDescription": "A workspace keeps your meetings and notes organized from the start.",
  "setup.readyTitle": "You're ready to remember more.",
  "setup.readyDescription": "Your workspace is ready. Open a Google Meet or Zoom Web meeting and Memoru will be there when captions are available.",
  "setup.openMeetings": "Open my meetings",
  "profileName.title": "What should we call you?",
  "profileName.description": "Your name appears on your meetings and to people in your workspace. You can change it later in Settings.",
  "profileName.label": "Full name",
  "profileName.placeholder": "e.g. Alex Morgan",
  "profileName.hint": "Use the name your teammates know you by.",
  "profileName.required": "Enter your name to continue.",
  "profileName.tooLong": "Use {max} characters or fewer.",
  "profileName.saveFailed": "Could not save your name. Try again.",
  "profileName.continue": "Continue",
  "profileName.signedInAs": "Signed in as {email}",
  "profileName.switchAccount": "Use a different account",
  "settingsPage.title": "Settings",
  "settingsPage.profile": "Profile",
  "settingsPage.sessions": "Active sessions",
  "settingsPage.signOutOthers": "Sign out other sessions",
  "settingsPage.noSessions": "No active sessions were returned.",
  "settingsPage.current": "Current",
  "settingsPage.revoke": "Revoke",
  "settingsPage.account": "Account",
  "settingsPage.signOut": "Sign out",
  "settingsPage.signOutDescription": "Ends your session on this device.",
  "settingsPage.proPlan": "Pro plan",
  "settingsPage.freePlan": "Free plan",
  "settingsPage.defaultName": "Memoru user",
  "settingsPage.loadError": "Could not load active sessions.",
  "settingsPage.revokeError": "Could not revoke this session.",
  "settingsPage.revokeOthersError": "Could not sign out the other sessions.",
  "settingsPage.extension": "Browser extension",
  "settingsPage.extensionChrome": "Browser extension · Chrome",
  "settingsPage.safariIphone": "Safari on iPhone",
  "settingsPage.mobileBrowser": "Mobile browser",
  "settingsPage.browserOnMac": "{browser} on macOS",
  "settingsPage.browserOnWindows": "{browser} on Windows",
  "settingsPage.browser": "Browser",
  "settingsPage.legacy": "Legacy client",
  "settingsPage.web": "Web browser",
  "settingsPage.recentlyActive": "Recently active",
  "settingsPage.activeNow": "Active now",
  "settingsPage.emailNotifications": "Email notifications",
  "settingsPage.overviewEmail": "Meeting overview ready",
  "settingsPage.overviewEmailOn": "Get an email when a meeting overview is ready to read.",
  "settingsPage.overviewEmailOff": "No email. Your overviews are still in Meetings.",
  "settingsPage.emailFooter": "Sent to {email}. Sign-in links are always sent.",
  "settingsPage.emailSaved": "Preference saved.",
  "settingsPage.emailSaveError": "Could not save your email preference. Try again.",
  "meetingStatus.live": "Live",
  "meetingStatus.ended": "Ended",
  "overviewStatus.ready": "Ready",
  "overviewStatus.generating": "Generating",
  "overviewStatus.failed": "Failed",
  "overviewStatus.none": "Not generated",
  "sort.newest": "Newest first",
  "sort.oldest": "Oldest first",
  "sort.segments": "Most segments",
  "datePreset.any": "Any time",
  "datePreset.today": "Today",
  "datePreset.7d": "Last 7 days",
  "datePreset.30d": "Last 30 days",
  "datePreset.90d": "Last 90 days",
  "datePreset.custom": "Custom range",
  "meetingList.loadingCount": "Loading meetings…",
  "meetingList.countFiltered": "{count} of {total} meetings",
  "meetingList.countMatching": "{count} matching meetings",
  "meetingList.count": {"one": "{count} meeting", "other": "{count} meetings"},
  "meetingList.segments": {"one": "{count} segment", "other": "{count} segments"},
  "meetingList.minutes": "{count} min",
  "meetingList.startedAt": "Started {time}",
  "meetingList.yesterday": "Yesterday, {time}",
  "meetingList.emptyDescription": "Join a call on Google Meet or Zoom with the Memoru extension installed, and it'll show up here automatically.",
  "meetingList.getExtension": "Get the extension",
  "meetingList.howItWorks": "How it works",
  "meetingList.howItWorksBody": "The browser extension listens for captions during your meeting. Memoru saves the transcript here, then prepares the recap after the call ends.",
  "meetingList.loadError": "Could not load meetings.",
  "meetingList.loadMoreError": "Could not load more meetings.",
  "meetingList.searchPlaceholder": "Search meetings…",
  "meetingList.appliedFilters": "Applied filters",
  "meetingList.removeFilter": "Remove {filter} filter",
  "meetingList.clearAll": "Clear all",
  "meetingList.unavailable": "Meetings are unavailable",
  "meetingList.noMatch": "No meetings match your search or filters.",
  "meetingList.noMatchHint": "Try a different search or clear the filters.",
  "meetingList.loadMore": "Load more",
  "meetingList.loadingMore": "Loading…",
  "filters.none": "No filters selected",
  "filters.selected": {"one": "{count} filter selected", "other": "{count} filters selected"},
  "filters.close": "Close filters",
  "filters.clear": "Clear",
  "filters.date": "Date",
  "filters.platform": "Platform",
  "filters.status": "Status",
  "filters.overview": "Overview",
  "filters.invalidRange": "Start date must be on or before the end date.",
  "filters.reset": "Reset",
  "filters.show": "Show results",
  "filters.isAnyOf": "is any of",
  "filters.is": "is",
  "filters.platformsCount": "{count} platforms",
  "filters.statusesCount": "{count} statuses",
  "detail.participants": "Participants",
  "detail.waitingParticipants": "Waiting for participants to speak.",
  "detail.info": "Meeting info",
  "detail.started": "Started",
  "detail.ended": "Ended",
  "detail.duration": "Duration",
  "detail.segments": "Segments",
  "detail.listening": "Listening for the conversation…",
  "detail.listeningBody": "Transcript segments will appear here as the meeting happens. The Overview tab unlocks once this meeting ends.",
  "detail.unknownSpeaker": "Unknown speaker",
  "detail.noTranscript": "No transcript captured",
  "detail.noTranscriptBody": "No caption segments were saved for this meeting.",
  "detail.endTranscript": "— End of transcript —",
  "detail.jump.missing": "That line is no longer in the transcript.",
  "detail.loadingMoreTranscript": "Loading more transcript…",
  "detail.loadingTranscript": "Loading transcript…",
  "detail.transcriptError": "Could not load the transcript.",
  "detail.search.label": "Search transcript",
  "detail.search.placeholder": "Search this meeting, even half a word",
  "detail.search.clear": "Clear search",
  "detail.search.previous": "Previous match",
  "detail.search.next": "Next match",
  "detail.search.position": "{index} of {count}",
  "detail.search.searching": "Searching…",
  "detail.search.noMatches": "No matches",
  "detail.search.noMatchesFor": "No matches for “{query}”.",
  "detail.search.noMatchesBody": "Try a shorter word or a different spelling.",
  "detail.search.error": "Could not search the transcript.",
  "detail.search.matchTypes": "Match type",
  "detail.search.all": "All",
  "detail.search.exact": "Exact",
  "detail.search.similar": "Similar",
  "detail.search.sounds": "Sounds like",
  "detail.search.onlyMatching": "Only matching lines",
  "detail.search.onlySpeaker": "Only {name}",
  "detail.search.showAllSpeakers": "Show all speakers",
  "detail.search.truncated": "This meeting is long, so only its first part was searched.",
  "detail.search.moreMatches": "Showing the best {count} of {total} matches.",
  "detail.view.open": "View options",
  "detail.view.chat": "Chat",
  "detail.view.chatBody": "Bubbles with avatars",
  "detail.view.lines": "Transcript",
  "detail.view.linesBody": "Timestamp column, like a script",
  "detail.view.compact": "Compact",
  "detail.view.compactBody": "Dense lines, most text per screen",
  "detail.view.merge": "Merge same speaker",
  "detail.view.mergeBody": "One message for back-to-back lines",
  "detail.view.timestamps": "Show timestamps",
  "detail.view.timestampsBody": "Time on each message",
  "detail.view.colors": "Color by speaker",
  "detail.view.colorsBody": "Turn off for plain names",
  "detail.view.reset": "Reset per-message views",
  "detail.message.merge": "Merge",
  "detail.message.toggle": "Switch this message between merged and separate segments",
  "detail.message.chat": "Chat",
  "detail.message.matchAt": "Match at {time} · view as segments",
  "detail.participants.total": "{count} participants",
  "detail.participants.filter": "Only show {name}",
  "detail.noSpeakerMessages": "No messages from this speaker.",
  "detail.loadingRecap": "Loading recap…",
  "detail.recapError": "Could not load the recap.",
  "detail.recapGenerating": "Generating recap",
  "detail.recapGeneratingBody": "Your recap will appear here automatically when it is ready.",
  "detail.recapFailed": "Recap generation failed",
  "detail.recapFailedBody": "The recap could not be generated. Check its status again or read the saved transcript.",
  "detail.recapSkipped": "No recap generated",
  "detail.recapSkippedBody": "This meeting did not qualify for a recap. You can still read its saved transcript.",
  "detail.noRecap": "Recap isn't available yet",
  "detail.noRecapBody": "A recap will appear here after the meeting ends and processing finishes. You can read the transcript while you wait.",
  "detail.readTranscript": "Read transcript",
  "detail.recap": "Recap",
  "detail.generated": "Generated {when}",
  "detail.recently": "Recently",
  "detail.decisions": "Decisions",
  "detail.aligned": "Aligned",
  "detail.noDecisions": "No decisions were recorded.",
  "detail.nextSteps": "Next steps",
  "detail.viewSource": "View source",
  "detail.noNextSteps": "No next steps were recorded.",
  "detail.loadError": "Could not load this meeting.",
  "detail.loadMoreError": "Could not load more transcript segments.",
  "detail.back": "Back to meetings",
  "titleMenu.renamed": "Meeting renamed",
  "titleMenu.renameError": "Could not rename this meeting.",
  "titleMenu.deleteError": "Could not delete this meeting.",
  "titleMenu.tooLong": "Meeting title can’t be longer than {max} characters ({count}/{max})",
  "titleMenu.hint": "Enter to save · Esc to cancel",
  "titleMenu.deleteTitle": "Delete this meeting?",
  "titleMenu.deleteBody": "“{title}” will be removed from {workspace} for everyone, along with its transcript and overview.",
  "titleMenu.thisWorkspace": "this workspace",
  "titleMenu.undo": "You can’t undo this.",
  "titleMenu.deleteConfirm": "Delete meeting",
  "titleMenu.actionsFor": "Meeting actions for {title}",
  "titleMenu.rename": "Rename",
  "titleMenu.delete": "Delete",
  "audio.player.label": "Meeting audio",
  "audio.player.play": "Play",
  "audio.player.pause": "Pause",
  "audio.player.speed": "Playback speed",
  "audio.player.speedValue": "{rate}×",
  "audio.player.position": "Audio position",
  "audio.player.positionText": "{now} of {length}",
  "audio.player.loadError": "Couldn't load the audio",
  "audio.line.play": "Play from {time}",
  "audio.line.noAudio": "No audio",
  "audio.divider.starts": "Audio starts at {time} · earlier lines have no audio",
  "audio.divider.notRecorded": "Not recorded {from}–{to}",
  "audio.divider.resumes": "Audio resumes at {time}",
  "audio.transcribing.pending": "Transcript from audio is on its way",
  "audio.toast.noAudioAt": "No audio at {time}. Audio starts at {start}.",
  "audio.toast.playFrom": "Play from {start}",
  "audio.toast.skipping": "Skipping {duration} that wasn't recorded",
  "audio.toast.finishingUpload": "Finishing the upload…",
  "audio.toast.retryFailed": "Couldn't start the transcript again. Try again later.",
  "audio.alert.uploading.title": "Audio is uploading",
  "audio.alert.uploading.progress": "Upload progress",
  "audio.alert.transcribing.title": "Turning audio into a transcript",
  "audio.alert.transcribing.body": "You can already listen. The transcript appears here when it is ready.",
  "audio.alert.failed.title": "Some of this audio isn't available yet",
  "audio.alert.failed.body": "Open memoru on the computer you recorded with, and we'll finish saving it.",
  "audio.alert.learnMore": "Learn more",
  "audio.alert.transcriptFailed.title": "We couldn't make a transcript",
  "audio.alert.transcriptFailed.body": "Your audio is safe and still plays. Try again, or come back later.",
  "audio.alert.partial.title": "This recording is incomplete",
  "audio.alert.partial.body": "The audio stops at {end} of a {length} minute meeting, so the transcript made from it ends there too.",
  "audio.alert.partial.bodyAudioOnly": "The audio stops at {end} of a {length} minute meeting.",
  "audio.alert.deleted": "This audio was deleted. The transcript and recap stay; times no longer play audio.",
  "app.navigationSidebar": "Navigation sidebar",
} satisfies Record<string, MessageValue>;

const id: typeof en = {
  "maintenance.title": "Sedang dalam pemeliharaan",
  "maintenance.description": "Memoru sedang diperbarui dan belum bisa dipakai sebentar. Silakan coba lagi nanti.",
  "maintenance.stillSignedIn": "Anda masih masuk. Kami akan mengembalikan Anda ke halaman tadi setelah selesai.",
  "maintenance.retry": "Periksa lagi",
  "maintenance.checking": "Memeriksa",
  "maintenance.autoCheck": "Halaman ini memeriksa sendiri dan membawa Anda kembali setelah selesai.",
  "maintenance.readOnlyTitle": "Hanya bisa dibaca untuk saat ini.",
  "maintenance.readOnlyBody": "Anda bisa membaca rapat Anda, tetapi perubahan dijeda selama kami memperbarui Memoru.",
  "maintenance.tag": "Pemeliharaan",
  "maintenance.language": "Bahasa",
  "maintenance.game.invite": "Main game singkat sambil menunggu.",
  "maintenance.game.label": "Game pola Echo",
  "maintenance.game.best": "Terbaik",
  "maintenance.game.round": "Ronde",
  "maintenance.game.play": "Main",
  "maintenance.game.again": "Lagi",
  "maintenance.game.idle": "Ulangi polanya. Sejauh mana Anda bisa?",
  "maintenance.game.watch": "Perhatikan polanya",
  "maintenance.game.yourTurn": "Giliran Anda",
  "maintenance.game.over": "Permainan selesai. Ronde selesai: {count}",
  "maintenance.game.how": "Ketuk bantalan, atau tekan 1 sampai 4 di keyboard.",
  "maintenance.game.sound": "Suara",
  "maintenance.game.pad1": "Lingkaran biru",
  "maintenance.game.pad2": "Persegi ungu",
  "maintenance.game.pad3": "Segitiga hijau",
  "maintenance.game.pad4": "Belah ketupat kuning",
  "app.account": "Akun",
  "app.activity": "Aktivitas",
  "app.availableLater": "Tersedia pada rilis mendatang",
  "app.commitments": "Komitmen",
  "app.collapseSidebar": "Ciutkan bilah samping",
  "app.customers": "Pelanggan",
  "app.experimentalAccess": "Akses eksperimental",
  "app.expandSidebar": "Luaskan bilah samping",
  "app.extensionSetup": "Penyiapan ekstensi",
  "app.meetings": "Rapat",
  "app.spaces": "Ruang",
  "app.openCaptureSetup": "Buka penyiapan tangkapan",
  "app.openNavigation": "Buka navigasi",
  "app.personalWorkspace": "Ruang kerja pribadi",
  "app.workspace": "Ruang kerja",
  "app.selectWorkspace": "Pilih ruang kerja",
  "app.createWorkspace": "Buat ruang kerja",
  "app.createWorkspaceDescription": "Buat tempat bersama untuk rapat, orang, dan tindak lanjut yang terkait.",
  "app.joinWorkspace": "Gabung ruang kerja",
  "app.workspaceActions": "Tindakan ruang kerja",
  "app.workspaceName": "Nama ruang kerja",
  "app.noWorkspaces": "Tidak ada ruang kerja",
  "app.sectionComingSoon": "Bagian ruang kerja ini sudah terhubung ke shell dan akan tersedia saat alur servernya selesai dikirim.",
  "app.backToToday": "Kembali ke Hari Ini",
  "onboarding.title": "Siapkan ruang kerja Anda",
  "onboarding.subtitle": "Luangkan beberapa menit untuk memahami cara Memoru menjaga detail rapat tetap berguna dan berada dalam kendali Anda.",
  "onboarding.step.language": "Bahasa dan pemrosesan",
  "onboarding.step.workspace": "Pilih ruang kerja",
  "onboarding.step.extension": "Hubungkan tangkapan",
  "onboarding.language.title": "Pilih bahasa Anda",
  "onboarding.language.description": "Ini mengatur antarmuka Memoru. Nama rapat, orang, dan bukti kutipan tetap dalam bentuk aslinya.",
  "onboarding.processing.title": "Data Anda, dijelaskan dengan jelas",
  "onboarding.processing.description": "Memoru menyimpan teks caption yang diizinkan ke ruang kerja pilihan Anda dan hanya menggunakan AI cloud untuk menyiapkan rangkuman. Pemrosesan AI dan retensi dapat diubah di Pengaturan.",
  "onboarding.workspace.title": "Di mana pekerjaan Anda disimpan?",
  "onboarding.workspace.description": "Rapat pribadi tetap privat. Ruang kerja tim hanya dibagikan sesuai keanggotaan dan visibilitas setiap catatan.",
  "onboarding.workspace.current": "Ruang kerja saat ini",
  "onboarding.workspace.createHint": "Buat ruang kerja tim dari pemilih ruang kerja saat Anda siap mengundang orang lain.",
  "onboarding.extension.title": "Hubungkan ekstensi tangkapan",
  "onboarding.extension.description": "Akun web adalah satu-satunya login memoru. Ekstensi menangkap caption secara lokal dan hanya menerima handoff koneksi singkat, bukan cookie refresh web Anda.",
  "onboarding.extension.connected": "Ekstensi tangkapan Anda sudah terhubung.",
  "onboarding.extension.notConnected": "Ekstensi belum terhubung. Anda dapat menyelesaikan penyiapan dan menghubungkannya nanti.",
  "onboarding.extension.expired": "Pemasangan ekstensi kedaluwarsa. Buka pengaturan ekstensi untuk menghubungkan ulang tanpa mengulang onboarding.",
  "onboarding.extension.captureRecovery": "Ada tangkapan sebelumnya yang perlu dipulihkan. Anda dapat melanjutkan atau memulainya kembali dari alur tangkapan.",
  "onboarding.openExtensionSetup": "Buka penyiapan ekstensi",
  "onboarding.back": "Kembali",
  "onboarding.next": "Lanjutkan",
  "onboarding.finish": "Selesaikan penyiapan",
  "onboarding.saving": "Menyimpan progres penyiapan",
  "onboarding.error": "Progres penyiapan tidak dapat disimpan. Coba lagi.",
  "workspace.joinTitle": "Gabung ruang kerja",
  "workspace.joinDescription": "Masukkan kode undangan dari pemilik atau administrator ruang kerja Anda.",
  "workspace.joinPrivacy": "Bergabung hanya menambahkan Anda ke ruang kerja tim. Rapat dan riwayat pribadi tetap terpisah.",
  "workspace.inviteCode": "Kode undangan",
  "workspace.previewInvite": "Pratinjau undangan",
  "workspace.previewTitle": "Detail undangan",
  "workspace.invitedBy": "Diundang oleh",
  "workspace.assignedRole": "Peran yang diberikan",
  "workspace.defaultVisibility": "Visibilitas bawaan",
  "workspace.previewExpires": "Berlaku sampai",
  "workspace.previewRequired": "Pratinjau undangan sebelum menerimanya.",
  "workspace.previewError": "Undangan ini tidak dapat dipratinjau.",
  "workspace.acceptError": "Undangan ini tidak dapat diterima.",
  "workspace.role.admin": "Administrator",
  "workspace.role.member": "Anggota",
  "workspace.role.billing_admin": "Administrator penagihan",
  "workspace.visibility.private": "Privat secara bawaan",
  "workspace.visibility.workspace": "Dibagikan ke ruang kerja secara bawaan",
  "workspace.acceptInvite": "Terima undangan",
  "workspace.backToWorkspace": "Kembali ke ruang kerja",
  "app.primaryNavigation": "Navigasi utama",
  "app.searchPlaceholder": "Cari apa pun di ruang kerja Anda…",
  "app.searchWorkspace": "Cari ruang kerja",
  "app.settings": "Pengaturan",
  "app.language": "Bahasa",
  "app.interface": "Antarmuka",
  "app.logOut": "Keluar",
  "app.closeSidebar": "Tutup bilah samping",
  "app.themeMenu": "Tema",
  "app.themeOptionLight": "Terang",
  "app.themeOptionDark": "Gelap",
  "app.themeOptionSystem": "Sistem",
  "app.theme": "Tampilan",
  "app.themeDark": "Mode gelap",
  "app.themeLight": "Mode terang",
  "app.themeSystem": "Mode sistem",
  "settings.eyebrow": "Pengaturan",
  "settings.title": "Atur Memoru sesuai cara kerja Anda.",
  "settings.subtitle": "Ruang kerja, aplikasi, dan batas Anda.",
  "settings.navLabel": "Bagian pengaturan",
  "settings.nav.account": "Akun",
  "settings.nav.connections": "Koneksi",
  "settings.nav.autonomy": "Izin agen",
  "settings.nav.members": "Anggota tim",
  "settings.nav.privacy": "Privasi & data",
  "settings.nav.billing": "Penggunaan & tagihan",
  "settings.connectionsNotice": "Menghubungkan alat tidak mengizinkan semua tindakan. Izin agen tetap berlaku.",
  "account.loading": "Memuat akun",
  "account.profileTitle": "Profil akun",
  "account.profileDescription": "Identitas yang sedang masuk dan detail paket Anda.",
  "account.email": "Email",
  "account.plan": "Paket",
  "account.summaries": "Ringkasan bulan ini",
  "account.experimental": "Akses eksperimental aktif. Penggunaan diukur, tetapi checkout, harga, faktur, perpanjangan, dan batas paket berbayar tidak ditampilkan.",
  "account.loginHistoryTitle": "Riwayat masuk",
  "account.loginHistoryDescription": "Tinjau sesi browser dan ekstensi yang masih dapat mengakses akun Anda.",
  "account.sessionsLoading": "Memuat riwayat masuk…",
  "account.sessionLoadError": "Riwayat masuk tidak dapat dimuat.",
  "account.sessionSignOutError": "Sesi tersebut tidak dapat dikeluarkan.",
  "account.noSessions": "Tidak ada sesi aktif untuk ditampilkan.",
  "account.session.web": "Aplikasi web",
  "account.session.extension": "Ekstensi Memoru",
  "account.session.legacy": "Masuk sebelumnya",
  "account.signedInAt": "Masuk pada {date}",
  "account.lastActiveAt": "Terakhir aktif {date}",
  "account.unknownDate": "waktu yang tidak diketahui",
  "account.signOutSession": "Keluar",
  "account.revokeSessionTitle": "Keluar dari sesi ini?",
  "account.revokeSessionDescription": "Tindakan ini akan mengeluarkan {device} pada perangkat tersebut. Sesi dapat masuk kembali saat diperlukan.",
  "account.signOutBrowserTitle": "Keluar dari browser ini",
  "account.signOutBrowserDescription": "Akhiri sesi Memoru saat ini di browser ini. Sesi lain tetap masuk.",
  "account.signOutBrowser": "Keluar",
  "app.skipToContent": "Lewati ke konten utama",
  "app.signedIn": "Sudah masuk",
  "app.signedInWorkspace": "Ruang kerja yang sudah dipilih",
  "app.today": "Hari ini",
  "app.workspaceNavigation": "Navigasi ruang kerja",
  "app.workspaceOwner": "Pemilik ruang kerja",
  "workspace.role.owner": "Pemilik ruang kerja",
  "meetings.subtitle": "Transkrip langsung dan sebelumnya dari ruang kerja ini.",
  "meetings.title": "Rapat",
  "meetings.directoryTitle": "Direktori rapat",
  "meetings.recordMeeting": "Rekam rapat",
  "meetings.recordMetadata": "Rekam metadata",
  "meetings.searchLabel": "Cari rapat",
  "meetings.searchPlaceholder": "Cari judul rapat, nama ruang, rekap...",
  "meetings.searching": "Mencari…",
  "meetings.filters": "Filter",
  "meetings.filterDescription": "Persempit rapat berdasarkan status, Ruang, platform, atau tanggal.",
  "meetings.activeFilters": "Filter rapat aktif",
  "meetings.clearFilters": "Hapus filter",
  "meetings.clearSearch": "Hapus pencarian",
  "meetings.loading": "Memuat rapat",
  "meetings.platform": "Platform",
  "meetings.dateRange": "Rentang tanggal",
  "meetings.dateFrom": "Dari",
  "meetings.dateTo": "Sampai",
  "meetings.pickDate": "Pilih tanggal",
  "meetings.clearDate": "Hapus tanggal",
  "meetings.done": "Selesai",
  "meetings.fromChip": "Dari {date}",
  "meetings.toChip": "Sampai {date}",
  "meetings.noSpaces": "Tidak ada Ruang aktif",
  "meetings.sortBy": "Urutkan:",
  "meetings.sortNewest": "Tanggal (Terbaru)",
  "meetings.sortOldest": "Tanggal (Terlama)",
  "meetings.sortTitle": "Judul",
  "meetings.sortStatus": "Status",
  "meetings.statusProcessed": "Diproses",
  "meetings.statusActionNeeded": "Perlu tindakan",
  "meetings.statusChip": "Status: Perlu tindakan",
  "meetings.columnTitle": "Judul rapat",
  "meetings.columnSpace": "Konteks ruang",
  "meetings.columnWhen": "Tanggal / Waktu",
  "meetings.spaceNone": "Tidak ada",
  "meetings.showing": "Menampilkan {from} sampai {to} dari {total} entri",
  "meetings.previous": "Sebelumnya",
  "meetings.next": "Berikutnya",
  "meetings.statusLabel": "Status",
  "meetings.statusAll": "Semua rapat",
  "meetings.statusLive": "Langsung",
  "meetings.statusEnded": "Selesai",
  "meetings.loadError": "Rapat tidak dapat dimuat",
  "meetings.emptyTitle": "Belum ada rapat",
  "meetings.noMatch": "Tidak ada rapat yang cocok",
  "meetings.loadMore": "Muat lebih banyak rapat",
  "meetings.emptyDescription": "Mulai tangkapan atau tinjau penyiapan contoh untuk memberi titik awal yang berguna bagi ruang kerja ini.",
  "meetings.noMatchDescription": "Coba pencarian lain atau hapus salah satu filter aktif.",
  "meetings.openMatch": "Buka kecocokan",
  "meetings.backToList": "Semua rapat",
  "spaces.title": "Ruang",
  "spaces.subtitle": "Atur rapat dan komitmen terkait tanpa menambah pekerjaan pada setiap rapat.",
  "spaces.create": "Buat Ruang",
  "spaces.edit": "Edit Ruang",
  "spaces.createDescription": "Beri nama yang jelas dan konteks opsional untuk Ruang ini.",
  "spaces.editDescription": "Perbarui nama atau konteks. Tautan yang ada tetap utuh.",
  "spaces.search": "Cari Ruang",
  "spaces.searchPlaceholder": "Cari berdasarkan nama atau deskripsi",
  "spaces.status": "Status",
  "spaces.active": "Aktif",
  "spaces.archived": "Diarsipkan",
  "spaces.all": "Semua",
  "spaces.sort": "Urutkan Ruang",
  "spaces.sortName": "Nama",
  "spaces.sortUpdated": "Baru diperbarui",
  "spaces.sortNewest": "Terbaru",
  "spaces.refreshing": "Memperbarui Ruang…",
  "spaces.name": "Nama",
  "spaces.description": "Deskripsi",
  "spaces.updated": "Diperbarui",
  "spaces.actions": "Tindakan",
  "spaces.actionsFor": "Tindakan untuk {name}",
  "spaces.noDescription": "Tanpa deskripsi",
  "spaces.namePlaceholder": "mis. Perpanjangan Acme",
  "spaces.descriptionPlaceholder": "Konteks opsional untuk pekerjaan ini",
  "spaces.save": "Simpan perubahan",
  "spaces.archive": "Arsipkan",
  "spaces.restore": "Pulihkan",
  "spaces.emptyTitle": "Tidak ada Ruang ditemukan",
  "spaces.emptySearch": "Coba pencarian atau status lain.",
  "spaces.emptyDescription": "Buat Ruang untuk menyatukan rapat dan komitmen terkait.",
  "spaces.loadError": "Ruang tidak dapat dimuat.",
  "spaces.createError": "Ruang ini tidak dapat dibuat.",
  "spaces.editError": "Ruang ini tidak dapat diperbarui. Muat ulang dan coba lagi.",
  "spaces.statusError": "Status Ruang tidak dapat diubah. Muat ulang dan coba lagi.",
  "spaces.noWorkspace": "Tidak ada ruang kerja yang dipilih",
  "spaces.noWorkspaceDescription": "Pilih ruang kerja untuk mengelola Ruang.",
  "spaceDetail.allSpaces": "Semua Ruang",
  "spaceDetail.loadError": "Ruang ini tidak dapat dimuat.",
  "spaceDetail.unavailable": "Ruang tidak tersedia",
  "spaceDetail.noDescription": "Belum ada deskripsi yang ditambahkan.",
  "spaceDetail.openCommitments": "Komitmen terbuka",
  "spaceDetail.noCommitments": "Tidak ada komitmen terbuka",
  "spaceDetail.noCommitmentsDescription": "Tindak lanjut terkonfirmasi yang ditautkan ke Ruang ini akan muncul di sini.",
  "spaceDetail.recentMeetings": "Rapat terbaru",
  "spaceDetail.noMeetings": "Belum ada rapat",
  "spaceDetail.noMeetingsDescription": "Rapat yang ditetapkan ke Ruang ini akan muncul di sini.",
  "spaceDetail.metadata": "Detail Ruang",
  "spaceDetail.created": "Dibuat",
  "spaceDetail.activity": "Aktivitas terbaru",
  "spaceDetail.noActivity": "Edit, tautan, dan perubahan status akan muncul di sini.",
  "today.handleFollowUp": "Tindak lanjuti",
  "meeting.handleFollowUp": "Tindak lanjut",
  "meeting.sectionsLabel": "Bagian rapat",
  "meeting.tab.overview": "Ikhtisar",
  "meeting.tab.decisions": "Keputusan",
  "meeting.tab.commitments": "Komitmen",
  "meeting.tab.transcript": "Transkrip",
  "meeting.tab.summary": "Ringkasan",
  "meeting.tab.activity": "Aktivitas",
  "meeting.tab.chat": "Tanya AI",
  "chat.title": "Tanya tentang rapat ini",
  "chat.subtitle": "Jawaban hanya berasal dari transkrip rapat ini.",
  "chat.newChat": "Obrolan baru",
  "chat.chats": "Obrolan",
  "chat.chatsButton": "Obrolan ({count})",
  "chat.chatsSheetDescription": "Pertanyaan Anda sebelumnya tentang rapat ini.",
  "chat.noChats": "Pertanyaan Anda tentang rapat ini akan tampil di sini.",
  "chat.loadingChats": "Memuat obrolan",
  "chat.chatsError": "Tidak dapat memuat obrolan Anda.",
  "chat.untitled": "Obrolan baru",
  "chat.messageCount": { one: "{count} pesan", other: "{count} pesan" },
  "chat.deleteChat": "Hapus obrolan",
  "chat.deleteTitle": "Hapus obrolan ini?",
  "chat.deleteBody": "“{title}” beserta jawabannya akan dihapus. Tindakan ini tidak dapat dibatalkan.",
  "chat.deleted": "Obrolan dihapus",
  "chat.deleteError": "Tidak dapat menghapus obrolan.",
  "chat.loadingMessages": "Memuat percakapan",
  "chat.messagesError": "Tidak dapat memuat percakapan ini.",
  "chat.promptsTitle": "Coba tanyakan",
  "chat.emptyTitle": "Apa yang ingin Anda ketahui?",
  "chat.emptyBody": "Tanyakan keputusan, penanggung jawab, atau tenggat. Setiap jawaban menunjuk ke momen saat hal itu dikatakan.",
  "chat.composerLabel": "Ajukan pertanyaan tentang rapat ini",
  "chat.placeholder": "Tanyakan keputusan, penanggung jawab, tenggat…",
  "chat.send": "Kirim",
  "chat.stop": "Berhenti",
  "chat.counter": "{count} / {max}",
  "chat.hint": "Enter untuk mengirim, Shift+Enter untuk baris baru",
  "chat.questionTooLong": "Pertanyaan maksimal {max} karakter.",
  "chat.busyHint": "Tunggu jawaban saat ini selesai, atau hentikan.",
  "chat.you": "Anda",
  "chat.assistant": "Memoru AI",
  "chat.phase.sending": "Mengirim…",
  "chat.phase.reading": "Membaca transkrip…",
  "chat.phase.writing": "Menulis…",
  "chat.answerReady": "Jawaban siap",
  "chat.stopped": "Dihentikan. Jawaban ini mungkin belum lengkap.",
  "chat.lengthCut": "Jawaban terpotong karena mencapai batas panjang.",
  "chat.stale": "Transkrip berubah setelah jawaban ini ditulis.",
  "chat.unverified": "Belum dicocokkan dengan transkrip. Periksa dulu sebelum mengandalkannya.",
  "chat.notInMeeting": "Tidak dibahas dalam rapat ini",
  "chat.claimSources": "Diucapkan oleh {sources}",
  "chat.speakerAt": "{speaker} pada {time}",
  "chat.sourcesMenu": "Sumber",
  "chat.sourcesTitle": "Dari transkrip",
  "chat.highlightToggle": "Sorot di dalam jawaban",
  "chat.viewInTranscript": "Lihat di transkrip",
  "chat.copy": "Salin jawaban",
  "chat.copied": "Tersalin",
  "chat.copyError": "Tidak dapat menyalin jawaban.",
  "chat.helpful": "Membantu",
  "chat.notHelpful": "Tidak membantu",
  "chat.feedbackError": "Tidak dapat menyimpan masukan Anda.",
  "chat.regenerate": "Buat ulang",
  "chat.jumpToLatest": "Terbaru",
  "chat.unavailable.live.title": "Tanya AI dibuka setelah rapat selesai",
  "chat.unavailable.live.body": "Transkrip masih ditulis. Kembali setelah rapat berakhir.",
  "chat.unavailable.noTranscript.title": "Belum ada yang bisa ditanyakan",
  "chat.unavailable.noTranscript.body": "Rapat ini tidak memiliki transkrip, jadi tidak ada dasar untuk menjawab.",
  "chat.unavailable.tooLong.title": "Rapat ini terlalu panjang untuk ditanyakan",
  "chat.unavailable.tooLong.body": "Transkrip lebih panjang dari yang dapat dibaca asisten sekaligus.",
  "chat.dailyLimit": "Anda sudah memakai semua {limit} pertanyaan hari ini. Anda dapat bertanya lagi {when}.",
  "chat.usage": "{used} dari {limit} pertanyaan terpakai hari ini",
  "chat.later": "nanti",
  "chat.chatFull": "Obrolan ini penuh. Mulai obrolan baru untuk terus bertanya.",
  "chat.failure.rate_limited": "Anda bertanya terlalu cepat. Coba lagi dalam {seconds} detik.",
  "chat.failure.rate_limited_soon": "Anda bertanya terlalu cepat. Coba lagi sebentar lagi.",
  "chat.failure.daily_limit": "Anda telah mencapai batas pertanyaan hari ini. Anda dapat bertanya lagi {when}.",
  "chat.failure.busy": "Jawaban lain masih ditulis. Tunggu sampai selesai.",
  "chat.failure.full": "Obrolan ini penuh. Mulai obrolan baru untuk terus bertanya.",
  "chat.failure.chat_limit": "Rapat ini sudah mencapai jumlah obrolan maksimum. Hapus satu untuk memulai yang baru.",
  "chat.failure.not_ready_live": "Pertanyaan dibuka setelah rapat berakhir.",
  "chat.failure.not_ready_no_transcript": "Rapat ini tidak memiliki transkrip untuk dijadikan dasar jawaban.",
  "chat.failure.too_long": "Rapat ini terlalu panjang untuk ditanyakan.",
  "chat.failure.validation": "Pertanyaan itu tidak dapat dikirim. Periksa lalu coba lagi.",
  "chat.failure.unavailable": "Layanan AI sedang tidak tersedia. Coba lagi sebentar lagi.",
  "chat.failure.timeout": "Jawaban terlalu lama. Coba lagi.",
  "chat.failure.not_found": "Obrolan ini sudah tidak tersedia.",
  "chat.failure.unauthorized": "Sesi Anda telah berakhir. Masuk lagi.",
  "chat.failure.network": "Koneksi terputus. Periksa jaringan Anda lalu coba lagi.",
  "chat.failure.unknown": "Terjadi kesalahan. Coba lagi.",
  "meeting.loading": "Memuat rapat",
  "meeting.unavailable": "Rapat tidak tersedia",
  "meeting.notFound": "Rapat ini tidak dapat ditemukan.",
  "meeting.noSpace": "Tanpa Ruang",
  "meeting.moreActions": "Tindakan rapat lainnya",
  "meeting.share": "Bagikan",
  "meeting.shareTitle": "Bagikan rapat yang telah ditinjau",
  "meeting.shareDescription": "Pilih dengan tepat apa yang dapat diakses anggota ruang kerja. Akses rekap saja tidak pernah menampilkan transkrip.",
  "meeting.shareError": "Rapat ini tidak dapat dibagikan.",
  "meeting.recipientLabel": "ID pengguna penerima",
  "meeting.recipientPlaceholder": "ID anggota ruang kerja",
  "meeting.shareInclude": "Sertakan",
  "meeting.shareRecapOnly": "Hanya rekap yang ditinjau",
  "meeting.shareFullTranscript": "Rekap dan transkrip lengkap",
  "meeting.titleLabel": "Judul rapat",
  "meeting.renameDescription": "Gunakan judul yang jelas agar mudah ditemukan nanti.",
  "meetingSpaces.title": "Penetapan Ruang",
  "meetingSpaces.description": "Pilih konteks aktif untuk rapat ini. Rapat juga dapat tetap tanpa Ruang.",
  "meetingSpaces.empty": "Belum ada Ruang yang tersedia.",
  "meetingSpaces.save": "Simpan Ruang",
  "meetingSpaces.saved": "Penetapan Ruang tersimpan.",
  "meetingSpaces.loadError": "Ruang tidak dapat dimuat.",
  "meetingSpaces.saveError": "Penetapan Ruang tidak dapat disimpan.",
  "meetingReport.insights": "Wawasan rapat",
  "meetingReport.insightsEmpty": "Keputusan, komitmen, dan pertanyaan yang belum terselesaikan akan muncul setelah ringkasan dibuat.",
  "meetingReport.summaryLabel": "Ringkasan rapat",
  "meetingReport.overviewLabel": "Ikhtisar rapat",
  "meetingReport.narrativeTitle": "Percakapan secara singkat",
  "meetingReport.narrativeDescription": "Rekap naratif ringkas yang berlandaskan transkrip.",
  "meetingReport.decisionsTitle": "Yang telah diputuskan",
  "meetingReport.decisionCount": { one: "{count} keputusan", other: "{count} keputusan" },
  "meetingReport.grounded": "Berlandaskan sumber rapat yang telah ditinjau.",
  "meetingReport.noDecisions": "Tidak ada keputusan yang dicatat.",
  "meetingReport.commitmentsTitle": "Komitmen untuk ditindaklanjuti",
  "meetingReport.noCommitments": "Tidak ada komitmen yang dicatat.",
  "meetingReport.prepared": "Disiapkan dari sumber",
  "meetingReport.unresolvedTitle": "Masih perlu diselesaikan",
  "meetingReport.noQuestions": "Tidak ada pertanyaan terbuka yang dicatat.",
  "meeting.activityTitle": "Aktivitas rapat",
  "meeting.activityDescription": "Peristiwa siklus hidup rapat ini. Tanda terima dan pemulihan alur tindak lanjut muncul saat proses tersedia.",
  "meeting.activityStarted": "Rapat dimulai",
  "meeting.activityEnded": "Rapat berakhir",
  "meeting.pauseLiveFollowing": "Jeda mengikuti siaran langsung",
  "meeting.followLiveTranscript": "Ikuti transkrip terbaru",
  "summary.optionsDescription": "Pilih bahasa dan bentuk rangkuman ini. Nama dan kutipan sumber tetap tidak berubah.",
  "summary.outputLanguage": "Bahasa keluaran",
  "summary.language.en": "English",
  "summary.language.id": "Bahasa Indonesia",
  "summary.language.mixed": "Campuran",
  "summary.template": "Templat rangkuman",
  "summary.template.general": "Rangkuman umum",
  "summary.template.customerCall": "Panggilan pelanggan",
  "summary.template.customerSuccess": "Kesuksesan pelanggan",
  "summary.regenerateReport": "Buat ulang rangkuman",
  "summary.chat.userRequest": "Permintaan pengguna",
  "summary.chat.assistant": "AI Memoru",
  "summary.chat.grounded": "Berlandaskan sumber",
  "summary.chat.sourceCitations": "Kutipan sumber",
  "summary.chat.starterTitle": "Prompt awal berlandaskan sumber",
  "summary.chat.prompt.summary": "Ringkasan terperinci",
  "summary.chat.prompt.decisions": "Jelaskan keputusan",
  "summary.chat.prompt.nextSteps": "Tinjau langkah berikutnya",
  "summary.chat.request.summary": "Berikan ringkasan terperinci dari rapat ini.",
  "summary.chat.request.decisions": "Keputusan apa yang dibuat dalam rapat ini?",
  "summary.chat.request.nextSteps": "Apa langkah berikutnya yang telah disepakati?",
  "summary.chat.composerPlaceholder": "Tanyakan tentang rapat ini kepada AI…",
  "summary.chat.ask": "Tanya",
  "summary.chat.unavailable": "Pertanyaan rapat terbuka memerlukan dukungan server dan belum tersedia.",
  "summary.chat.reportTools": "Alat laporan",
  "summary.chat.reportMeta": "Laporan v{version} · {status} · {language}",
  "summary.chat.compareFrom": "Bandingkan dari versi laporan",
  "summary.chat.compare": "Bandingkan",
  "summary.chat.approve": "Setujui",
  "summary.chat.editSummary": "Edit ringkasan",
  "summary.chat.reviewedRecap": "Rekap yang telah ditinjau",
  "summary.chat.saveCorrection": "Simpan koreksi",
  "summary.chat.changesFrom": "Perubahan dari versi {version}",
  "summary.chat.noChanges": "Tidak ada perubahan berarti.",
  "meeting.staleReport": "Transkrip berubah sejak rangkuman ini",
  "meeting.staleReportDescription": "Tinjau bukti dan perbarui rangkuman sebelum menggunakannya untuk tindak lanjut.",
  "meeting.reviewStale": "Tinjau rangkuman",
  "customers.subtitle": "Simpan konteks pelanggan di ruang kerja dengan hubungan yang eksplisit.",
  "customers.searchLabel": "Cari pelanggan",
  "customers.searchPlaceholder": "Cari berdasarkan nama atau domain",
  "customers.createTitle": "Tambah pelanggan",
  "customers.name": "Nama pelanggan",
  "customers.namePlaceholder": "Acme, Inc.",
  "customers.domain": "Domain (opsional)",
  "customers.create": "Tambah pelanggan",
  "customers.loading": "Memuat pelanggan",
  "customers.loadError": "Pelanggan tidak dapat dimuat",
  "customers.createError": "Pelanggan tidak dapat dibuat",
  "customers.updateError": "Pelanggan tidak dapat diperbarui",
  "customers.editTitle": "Edit pelanggan",
  "customers.edit": "Edit pelanggan",
  "customers.cancelEdit": "Batalkan pengeditan",
  "customers.save": "Simpan pelanggan",
  "customers.historyTitle": "Riwayat pelanggan",
  "customers.noHistory": "Belum ada riwayat pelanggan.",
  "customers.emptyTitle": "Belum ada pelanggan",
  "customers.noMatch": "Tidak ada pelanggan yang cocok",
  "customers.emptyDescription": "Buat pelanggan untuk menjaga konteks rapat tetap eksplisit dan dapat ditinjau.",
  "customers.backToList": "Kembali ke pelanggan",
  "customers.noDomain": "Belum ada domain",
  "customers.meetingsTitle": "Rapat tertaut",
  "customers.noMeetings": "Belum ada rapat yang tertaut ke pelanggan ini.",
  "customers.commitmentsTitle": "Komitmen terbuka",
  "customers.noCommitments": "Belum ada komitmen yang tertaut ke pelanggan ini.",
  "customers.findDuplicates": "Periksa duplikat",
  "customers.mergeTitle": "Tinjau penggabungan pelanggan",
  "customers.closeMerge": "Tutup tinjauan penggabungan",
  "customers.mergeWarning": "Pelanggan yang mirip tidak pernah digabung otomatis. Tinjau sumber, target, dan konflik sebelum mengonfirmasi.",
  "customers.loadingMergeCandidates": "Memuat kemungkinan duplikat",
  "customers.mergeTarget": "Pelanggan target",
  "customers.chooseCustomer": "Pilih pelanggan",
  "customers.noMergeCandidates": "Tidak ada kemungkinan pelanggan duplikat.",
  "customers.previewMerge": "Pratinjau penggabungan",
  "customers.mergePreview": "Pratinjau penggabungan",
  "customers.noMergeConflicts": "Tidak ada konflik. Penggabungan tetap memerlukan konfirmasi eksplisit.",
  "customers.confirmMerge": "Konfirmasi penggabungan",
  "customers.mergeError": "Tinjauan atau penggabungan pelanggan gagal",
  "followThrough.title": "Tindak lanjut",
  "followThrough.description": "Asosiasi dan komitmen tetap eksplisit, dapat ditinjau, dan terpisah dari rangkuman rapat.",
  "followThrough.customerTitle": "Asosiasi pelanggan",
  "followThrough.customerLabel": "Pelanggan",
  "followThrough.noCustomer": "Belum ada pelanggan yang dipilih",
  "followThrough.confirmAssociation": "Konfirmasi asosiasi",
  "followThrough.associated": "Asosiasi tersimpan",
  "followThrough.commitmentTitle": "Komitmen",
  "followThrough.textLabel": "Teks komitmen",
  "followThrough.textPlaceholder": "Jelaskan tindakan berikutnya",
  "followThrough.dueLabel": "Frasa tenggat (opsional)",
  "followThrough.duePlaceholder": "sebelum Jumat",
  "followThrough.suggest": "Simpan sebagai saran",
  "followThrough.suggested": "Disarankan",
  "followThrough.confirm": "Konfirmasi komitmen",
  "followThrough.confirmed": "Terkonfirmasi",
  "followThrough.complete": "Tandai selesai",
  "followThrough.reopen": "Buka kembali",
  "followThrough.noDueDate": "Tanpa tenggat",
  "followThrough.shareTitle": "Bagikan rangkuman terverifikasi",
  "followThrough.shareDescription": "Ini hanya memberikan akses rangkuman. Transkrip dan rekaman tidak ikut terbuka.",
  "followThrough.recipientLabel": "ID pengguna penerima",
  "followThrough.recipientPlaceholder": "ID anggota ruang kerja",
  "followThrough.noRecipient": "Pilih anggota ruang kerja",
  "followThrough.audienceLabel": "Akses yang dibagikan",
  "followThrough.audience.recap": "Hanya rangkuman terverifikasi",
  "followThrough.audience.transcript": "Rangkuman dan transkrip terverifikasi",
  "followThrough.audience.recapDescription": "Penerima dapat membaca rangkuman terstruktur yang ditinjau, tetapi tidak dapat melihat transkrip atau rekaman.",
  "followThrough.audience.transcriptDescription": "Penerima dapat membaca rangkuman terverifikasi dan transkrip lengkap. Akses rekaman tetap terpisah.",
  "followThrough.shareRecap": "Bagikan rangkuman saja",
  "followThrough.shareTranscript": "Bagikan rangkuman dan transkrip",
  "followThrough.sharedRecap": "Rangkuman dibagikan kepada",
  "followThrough.associationError": "Asosiasi pelanggan tidak dapat disimpan",
  "followThrough.removeAssociation": "Hapus asosiasi",
  "followThrough.removeAssociationError": "Asosiasi pelanggan tidak dapat dihapus",
  "followThrough.suggestionError": "Saran komitmen tidak dapat disimpan",
  "followThrough.confirmationError": "Komitmen tidak dapat dikonfirmasi",
  "followThrough.statusError": "Komitmen tidak dapat diperbarui",
  "followThrough.shareError": "Rangkuman tidak dapat dibagikan",
  "followThrough.revokeError": "Akses rangkuman tidak dapat dicabut",
  "followThrough.unknownRecipient": "Anggota tidak dikenal",
  "followThrough.revoked": "Dicabut",
  "followThrough.revoke": "Cabut akses",
  "commitments.subtitle": "Lacak komitmen terkonfirmasi dengan status, pemilik, konteks tenggat, dan riwayat.",
  "commitments.loading": "Memuat komitmen",
  "commitments.loadError": "Komitmen tidak dapat dimuat",
  "commitments.statusError": "Komitmen tidak dapat diperbarui",
  "commitments.historyError": "Riwayat komitmen tidak dapat dimuat",
  "commitments.statusLabel": "Tampilkan komitmen",
  "commitments.status.open": "Terbuka",
  "commitments.status.completed": "Selesai",
  "commitments.status.cancelled": "Dibatalkan",
  "commitments.status.all": "Semua",
  "commitments.emptyTitle": "Belum ada komitmen",
  "commitments.emptyDescription": "Komitmen terkonfirmasi dari rapat yang ditinjau akan muncul di sini.",
  "commitments.openMeeting": "Buka rapat sumber",
  "commitments.owner": "Pemilik",
  "commitments.customer": "Pelanggan",
  "commitments.noCustomer": "Tanpa pelanggan",
  "commitments.unassigned": "Belum ditugaskan",
  "commitments.noDate": "Tanpa tanggal",
  "commitments.complete": "Tandai selesai",
  "commitments.reopen": "Buka kembali",
  "commitments.showHistory": "Tampilkan riwayat",
  "commitments.hideHistory": "Sembunyikan riwayat",
  "commitments.edit": "Edit komitmen",
  "commitments.editTitle": "Edit komitmen",
  "commitments.closeEditor": "Tutup editor komitmen",
  "commitments.editError": "Komitmen tidak dapat diedit",
  "commitments.text": "Komitmen",
  "commitments.duePhrase": "Frasa tenggat asli",
  "commitments.dueDate": "Tanggal tenggat",
  "commitments.timezone": "Zona waktu",
  "commitments.taskLinks": "Tautan tugas eksternal",
  "commitments.taskProvider": "Penyedia tugas",
  "commitments.taskId": "ID tugas eksternal",
  "commitments.taskUrl": "URL tugas (opsional)",
  "commitments.addTask": "Tambah tautan tugas",
  "commitments.taskError": "Tautan tugas eksternal tidak dapat diperbarui",
  "commitments.save": "Simpan komitmen",
  "activity.subtitle": "Run alur kerja, kuitansi, kegagalan, dan tindakan pemulihan yang otoritatif.",
  "activity.loading": "Memuat aktivitas",
  "activity.loadError": "Aktivitas tidak dapat dimuat",
  "activity.retryError": "Pekerjaan tersisa tidak dapat dicoba ulang",
  "activity.runsTitle": "Run alur kerja",
  "activity.noRuns": "Belum ada run alur kerja.",
  "activity.timelineTitle": "Linimasa aktivitas",
  "activity.noActivity": "Belum ada aktivitas alur kerja.",
  "activity.reconciliationRequired": "Rekonsiliasi diperlukan",
  "activity.retry": "Coba ulang tersisa ({count})",
  "activity.receipts": "Kuitansi",
  "activity.verified": "terverifikasi",
  "activity.failed": "gagal",
  "activity.reconciling": "direkonsiliasi",
  "activity.recorded": "tercatat",
  "activity.createGoal": "Buat tujuan untuk ditinjau",
  "autonomy.title": "Kebijakan ruang kerja",
  "autonomy.subtitle": "Kelola kontrol ruang kerja terbatas yang dievaluasi server.",
  "autonomy.loading": "Memuat kebijakan ruang kerja",
  "autonomy.loadError": "Kebijakan ruang kerja tidak dapat dimuat",
  "autonomy.unavailable": "Kebijakan ruang kerja tidak tersedia.",
  "autonomy.updateError": "Kebijakan ruang kerja tidak dapat diperbarui",
  "autonomy.backToSettings": "Kembali ke pengaturan",
  "autonomy.visibilityTitle": "Visibilitas bawaan",
  "autonomy.visibilityDescription": "Pilih bawaan yang diterapkan server untuk konten ruang kerja baru.",
  "autonomy.crmTitle": "Catatan dan tugas CRM",
  "autonomy.crmDescription": "Izinkan alur kerja yang disetujui membuat catatan dan tugas CRM saat kebijakan server mengizinkan.",
  "autonomy.crmLabel": "Izinkan catatan dan tugas CRM",
  "autonomy.emailLabel": "Pengiriman email",
  "autonomy.reviewRequired": "Tinjauan diperlukan",
  "autonomy.adminOnly": "Hanya pemilik dan admin ruang kerja yang dapat mengubah kebijakan.",
  "autonomy.saving": "Menyimpan kebijakan",
  "goal.title": "Tinjauan tujuan",
  "goal.loading": "Memuat tinjauan tujuan",
  "goal.loadError": "Tinjauan tujuan tidak dapat dimuat",
  "goal.unavailable": "Tinjauan tujuan ini tidak tersedia.",
  "goal.mutationError": "Tinjauan tindakan tidak dapat diperbarui",
  "goal.backToActivity": "Kembali ke aktivitas",
  "goal.policyVersion": "Versi kebijakan",
  "goal.stepsTitle": "Langkah yang dapat dipilih",
  "goal.edit": "Tinjau langkah",
  "goal.selected": "Dipilih untuk persetujuan",
  "goal.manual": "Manual atau dikecualikan",
  "goal.evidence": "Bukti",
  "goal.editTitle": "Edit tindakan",
  "goal.reason": "Alasan",
  "goal.payload": "Payload tindakan yang tepat",
  "goal.saveDraft": "Simpan langkah revisi",
  "goal.keepManual": "Pertahankan manual",
  "goal.reject": "Tolak langkah",
  "goal.createTitle": "Buat tujuan untuk ditinjau",
  "goal.createDescription": "Berikan provenance server dan tindakan pertama yang tepat. Server akan mengevaluasi kebijakan sebelum eksekusi.",
  "goal.createError": "Tujuan tidak dapat dibuat",
  "goal.titleLabel": "Judul tujuan",
  "goal.objective": "Objektif",
  "goal.provenanceTitle": "Provenance sumber",
  "goal.sourceVersion": "Versi sumber",
  "goal.sourceDigest": "Digest sumber",
  "goal.stepTitle": "Langkah pertama untuk ditinjau",
  "goal.kind": "Jenis tindakan",
  "goal.create": "Buat tujuan untuk ditinjau",
  "billing.usageTitle": "Anggaran penggunaan saat ini",
  "billing.usageUnits": "unit digunakan",
  "billing.usageRemaining": "tersisa",
  "billing.usageReserved": "Dicadangkan",
  "billing.actionsTitle": "Tindakan penagihan",
  "billing.checkout": "Buka checkout",
  "billing.portal": "Buka portal penagihan",
  "billing.actionUnavailable": "Tindakan penagihan ini tidak tersedia.",
  "workflow.retryError": "Pekerjaan tersisa tidak dapat dicoba ulang",
  "workflow.recoveryTitle": "Pulihkan pekerjaan tersisa",
  "workflow.recoveryDescription": "Hanya pekerjaan gagal dengan persetujuan yang tidak berubah dan masih valid yang dapat diantrekan. Kuitansi berhasil tetap terlihat.",
  "workflow.retryRemaining": "Coba ulang tersisa",
  "workflow.reconciliationTitle": "Rekonsiliasi",
  "workflow.reconciliationDescription": "Hasil dari penyedia belum pasti. Jangan kirim ulang sampai rekonsiliasi selesai.",
  "workflow.reconciliationClear": "Tidak ada hasil penyedia yang belum terselesaikan.",
  "workflow.title": "Proses alur kerja",
  "workflow.subtitle": "Tinjau tindakan, efek, tanda terima, dan status pemulihan yang otoritatif.",
  "workflow.loading": "Memuat proses alur kerja",
  "workflow.loadError": "Proses alur kerja tidak dapat dimuat",
  "workflow.unavailable": "Proses alur kerja ini tidak tersedia.",
  "workflow.updateError": "Proses alur kerja tidak dapat diperbarui",
  "workflow.backToActivity": "Kembali ke aktivitas",
  "workflow.state": "Status",
  "workflow.pause": "Jeda proses",
  "workflow.resume": "Lanjutkan proses",
  "workflow.cancel": "Batalkan pekerjaan berikutnya",
  "workflow.approvalUnavailable": "Proses ini perlu ditinjau, tetapi respons server belum menyertakan versi kebijakan yang diperlukan untuk mengikat persetujuan. Kontrol persetujuan tidak diaktifkan.",
  "workflow.approvalError": "Tindakan persis ini tidak dapat disetujui",
  "workflow.policySummary": "Kebijakan server",
  "workflow.emailReviewRequired": "pengiriman email perlu ditinjau",
  "workflow.emailReviewOptional": "peninjauan pengiriman email tidak diwajibkan",
  "workflow.maxMutations": "maksimum mutasi eksternal",
  "workflow.approve": "Setujui tindakan persis",
  "workflow.actionsTitle": "Tindakan",
  "workflow.requiresApproval": "perlu persetujuan",
  "workflow.noApproval": "tidak perlu persetujuan",
  "workflow.sourceVersion": "Versi sumber",
  "workflow.payloadHash": "Hash muatan",
  "workflow.receiptsTitle": "Tanda terima",
  "workflow.noReceipts": "Belum ada tanda terima.",
  "workflow.activityTitle": "Aktivitas",
  "workflow.recorded": "tercatat",
  "billing.title": "Status penagihan",
  "billing.subtitle": "Lihat status komersial otoritatif dari server untuk ruang kerja ini.",
  "billing.loading": "Memuat status penagihan",
  "billing.loadError": "Status penagihan tidak dapat dimuat",
  "billing.unavailable": "Status penagihan tidak tersedia",
  "billing.unavailableDescription": "Status penagihan tidak tersedia untuk ruang kerja ini.",
  "billing.backToSettings": "Kembali ke pengaturan",
  "billing.accessTitle": "Akses komersial",
  "billing.mode": "Mode",
  "billing.state": "Status",
  "billing.entitled": "Fitur komersial saat ini berhak digunakan.",
  "billing.notEntitled": "Fitur komersial saat ini tidak berhak digunakan.",
  "billing.mode.bypass": "Akses eksperimental",
  "billing.mode.shadow": "Evaluasi bayangan",
  "billing.mode.enforced": "Penagihan diberlakukan",
  "billing.state.no_subscription": "Tanpa langganan",
  "billing.state.trialing": "Masa uji coba",
  "billing.state.active": "Aktif",
  "billing.state.grace": "Masa tenggang",
  "billing.state.payment_failed": "Pembayaran gagal",
  "billing.state.cancellation_scheduled": "Pembatalan terjadwal",
  "billing.state.paused": "Dijeda",
  "billing.state.unpaid": "Belum dibayar",
  "billing.state.cancelled": "Dibatalkan",
  "billing.state.expired": "Kedaluwarsa",
  "billing.state.incomplete": "Belum lengkap",
  "billing.state.incomplete_expired": "Belum lengkap dan kedaluwarsa",
  "billing.subscriptionTitle": "Langganan",
  "billing.plan": "Paket",
  "billing.status": "Status",
  "billing.amount": "Jumlah",
  "billing.interval": "Interval penagihan",
  "billing.seats": "Kursi",
  "billing.periodEnd": "Periode saat ini berakhir",
  "billing.noDate": "Tanpa tanggal",
  "billing.cancellationScheduled": "Pembatalan dijadwalkan pada akhir periode saat ini.",
  "billing.renewalExpected": "Perpanjangan saat ini diharapkan sesuai status server.",
  "billing.noSubscriptionTitle": "Tanpa langganan",
  "billing.noSubscriptionDescription": "Ruang kerja ini saat ini tidak memiliki catatan langganan.",
  "billing.actionsPending": "Checkout, portal, faktur, dan kontrol penggunaan belum tersedia sampai kontrak servernya siap.",
  "members.title": "Anggota ruang kerja",
  "members.subtitle": "Tinjau keanggotaan aktif dan perbarui peran tanpa masuk ulang.",
  "members.loading": "Memuat anggota ruang kerja",
  "members.loadError": "Anggota ruang kerja tidak dapat dimuat",
  "members.roleError": "Peran anggota tidak dapat diperbarui",
  "members.revokeError": "Keanggotaan ruang kerja tidak dapat dicabut",
  "members.revokeConfirm": "Cabut akses ruang kerja anggota ini?",
  "members.backToSettings": "Kembali ke pengaturan",
  "members.owner": "Pemilik ruang kerja",
  "members.role": "Peran",
  "members.admin": "Administrator",
  "members.member": "Anggota",
  "members.billingAdmin": "Administrator penagihan",
  "members.revoke": "Cabut akses",
  "members.readOnly": "Hanya baca",
  "members.defaultVisibility": "Visibilitas default",
  "privacy.title": "Kontrol privasi",
  "privacy.subtitle": "Atur pemrosesan AI, retensi, ekspor, dan penghapusan akun Anda.",
  "privacy.loading": "Memuat kontrol privasi",
  "privacy.loadError": "Kontrol privasi tidak dapat dimuat",
  "privacy.processingTitle": "Pemrosesan AI dan retensi",
  "privacy.processingDescription": "Menonaktifkan pemrosesan AI mencegah ringkasan AI baru. Rekaman yang masih disimpan tidak dihapus diam-diam.",
  "privacy.processingLabel": "Izinkan AI cloud menyiapkan ringkasan rapat",
  "privacy.retentionLabel": "Simpan rekaman selama (hari)",
  "privacy.retentionError": "Retensi harus antara 1 hingga 3650 hari.",
  "privacy.save": "Simpan pengaturan privasi",
  "privacy.saveError": "Pengaturan privasi tidak dapat disimpan",
  "privacy.exportTitle": "Ekspor data Anda",
  "privacy.exportDescription": "Unduh ekspor JSON tanpa penyimpanan berisi profil, pengaturan privasi, rapat, bukti transkrip, dan laporan.",
  "privacy.export": "Unduh ekspor privasi",
  "privacy.exportError": "Data Anda tidak dapat diekspor",
  "privacy.deleteTitle": "Hapus akun",
  "privacy.deleteDescription": "Penghapusan menghapus konten akun yang dapat digunakan dan mengantrekan pembersihan. Salinan eksternal, cadangan, dan rekaman pengguna lain berada di luar operasi ini.",
  "privacy.deleteConfirmation": "Ketik DELETE MY ACCOUNT untuk mengonfirmasi",
  "privacy.delete": "Hapus akun",
  "privacy.deleteError": "Akun Anda tidak dapat dihapus",
  "privacy.deletedTitle": "Akun dihapus",
  "privacy.deletedDescription": "Konten akun Anda telah dihapus dari tampilan yang dapat digunakan. Lanjutkan untuk menyelesaikan proses keluar.",
  "privacy.deletionDetailsTitle": "Status penghapusan",
  "privacy.deletionScope": "Cakupan yang dihapus",
  "privacy.deletionPending": "Tugas pembersihan yang tertunda",
  "privacy.deletionExternalCopies": "Salinan eksternal",
  "privacy.externalCopiesNotTracked": "Tidak dilacak oleh Memoru",
  "privacy.externalCopiesReconciliation": "Perlu rekonsiliasi",
  "privacy.deletionBackupExpiry": "Kedaluwarsa cadangan",
  "privacy.deletionDevicePurge": "Pembersihan perangkat",
  "privacy.continueAfterDeletion": "Lanjutkan",
  "privacy.backToSettings": "Kembali ke pengaturan",
  "connections.title": "Koneksi bisnis",
  "connections.subtitle": "Hubungkan alat bisnis yang disetujui untuk tindak lanjut ruang kerja.",
  "connections.loading": "Memuat koneksi bisnis",
  "connections.loadError": "Koneksi bisnis tidak dapat dimuat",
  "connections.connectError": "Koneksi tidak dapat dimulai",
  "connections.healthError": "Kesehatan konektor tidak dapat diperiksa",
  "connections.disconnectError": "Konektor ini tidak dapat diputuskan",
  "connections.backToSettings": "Kembali ke pengaturan",
  "connections.availableTitle": "Terhubung ke pekerjaan Anda",
  "connections.availableDescription": "Pilih tempat Memoru dapat membaca konteks dan menjalankan tindakan.",
  "connections.connectGmail": "Hubungkan Gmail",
  "connections.connectHubspot": "Hubungkan HubSpot",
  "connections.emptyTitle": "Belum ada koneksi bisnis",
  "connections.emptyDescription": "Hubungkan Gmail atau HubSpot saat Anda siap menggunakan tindak lanjut ruang kerja.",
  "connections.accountType": "Jenis akun",
  "connections.apiVersion": "Versi API",
  "connections.health": "Kesehatan",
  "connections.scopes": "Cakupan yang diberikan",
  "connections.refreshHealth": "Periksa kesehatan",
  "connections.disconnect": "Putuskan",
  "connections.disconnectConfirm": "Putuskan konektor bisnis ini?",
  "connections.status.active": "Aktif",
  "connections.status.revoked": "Dicabut",
  "connections.status.expired": "Kedaluwarsa",
  "connections.status.reconciling": "Rekonsiliasi",
  "connections.health.unknown": "Tidak diketahui",
  "connections.health.healthy": "Sehat",
  "connections.health.unhealthy": "Tidak sehat",
  "connections.health.reconciling": "Rekonsiliasi",
  "today.title": "Hari ini",
  "today.subtitle": "Tampilan terarah untuk pekerjaan yang perlu diperhatikan di ruang kerja ini.",
  "today.needsReview": "Perlu ditinjau",
  "today.blocked": "Terblokir",
  "today.due": "Segera jatuh tempo",
  "today.recentVerified": "Baru terverifikasi",
  "today.followUp": "Tindak lanjuti",
  "today.sectionEmpty": "Belum ada apa pun di bagian ini.",
  "today.emptyTitle": "Belum ada yang perlu diperhatikan",
  "today.emptyDescription": "Mulai tangkapan atau tinjau penyiapan contoh untuk memberi titik awal yang berguna bagi ruang kerja ini.",
  "today.noWorkspace": "Pilih ruang kerja",
  "today.noWorkspaceDescription": "Pilih ruang kerja aktif sebelum melihat Hari ini.",
  "today.loadError": "Hari ini tidak dapat dimuat",
  "today.reason.deadline": "Tenggat",
  "today.reason.dependency": "Dependensi",
  "today.reason.recency": "Terbaru",
  "today.resource.meeting": "Rapat",
  "today.resource.customer": "Pelanggan",
  "today.resource.space": "Ruang",
  "today.resource.commitment": "Komitmen",
  "today.resource.activity": "Aktivitas",
  "today.resource.goal": "Tujuan",
  "search.title": "Pencarian",
  "search.subtitle": "Hasil untuk “{query}”",
  "search.emptyTitle": "Cari di ruang kerja Anda",
  "search.emptyDescription": "Cari rapat, pelanggan, komitmen, dan segmen bukti yang tepat.",
  "search.dialogTitle": "Pencarian ruang kerja",
  "search.clear": "Hapus pencarian",
  "search.searching": "Mencari di ruang kerja Anda…",
  "search.noResults": "Tidak ada hasil",
  "search.noResultsDescription": "Coba frasa lain atau periksa ruang kerja lain.",
  "search.loadError": "Ruang kerja ini tidak dapat dicari",
  "search.kind.customer": "Pelanggan",
  "search.kind.space": "Ruang",
  "search.kind.meeting": "Rapat",
  "search.kind.commitment": "Komitmen",
  "search.kind.segment": "Bukti",
  "common.close": "Tutup",
  "common.cancel": "Batal",
  "common.create": "Buat",
  "common.language": "Bahasa",
  "common.loading": "Memuat",
  "common.retry": "Coba lagi",
  "error.goBack": "Kembali",
  "error.goHome": "Buka Hari ini",
  "error.notFound.description": "Halaman mungkin telah dipindahkan, dihapus, atau alamatnya tidak tepat.",
  "error.notFound.title": "Halaman tidak ditemukan",
  "error.page.description": "Kesalahan tak terduga menghentikan pemuatan halaman ini. Coba lagi atau kembali ke Hari ini.",
  "error.page.title": "Halaman ini tidak dapat dimuat",
  "notifications.dismiss": "Tutup notifikasi",
  "notifications.label": "Notifikasi",
  "locale.en": "English",
  "locale.id": "Bahasa Indonesia",
  "meeting.sourceLabel": "Sumber: {source}",
  "meeting.rename": "Ubah nama rapat",
  "meeting.renameSave": "Simpan judul",
  "meeting.renameCancel": "Batal",
  "meeting.renameError": "Nama rapat tidak dapat diubah",
  "meeting.delete": "Hapus rapat",
  "meeting.deleteShort": "Hapus",
  "meeting.deleteDialogTitle": "Hapus rapat ini?",
  "meeting.deleteWarningTitle": "Tindakan ini bersifat permanen",
  "meeting.deleteDescription": "“{title}” akan dihapus dari ruang kerja Anda dan tidak dapat dipulihkan.",
  "meeting.deleteScopeTitle": "Data berikut akan dihapus",
  "meeting.deleteScopeMeeting": "Detail dan judul rapat",
  "meeting.deleteScopeTranscript": "Transkrip, rangkuman, dan laporan yang dibuat",
  "meeting.deleteScopeEvidence": "Bukti tersimpan dan status pemrosesan",
  "meeting.deleteConfirmationLabel": "Ketik “Delete” untuk mengonfirmasi",
  "meeting.deleteConfirmationHint": "Langkah tambahan ini membantu mencegah penghapusan yang tidak disengaja.",
  "meeting.deleteConfirm": "Hapus permanen",
  "meeting.deleteCancel": "Pertahankan rapat",
  "meeting.deleteError": "Rapat tidak dapat dihapus. Rapat Anda masih aman. Coba lagi.",
  "meeting.transcriptOriginal": "Transkrip asli",
  "meeting.transcriptDescription": "Teks caption · tanpa rekaman audio atau video",
  "meeting.transcriptUpdated": "Diperbarui {time}",
  "meeting.transcriptWaiting": "Menunggu segmen transkrip…",
  "meeting.transcriptLoadingMore": "Memuat transkrip lainnya…",
  "meeting.metadata.title": "Metadata rapat",
  "meeting.metadata.meetingType": "Jenis rapat",
  "meeting.metadata.videoSync": "Sinkronisasi video",
  "meeting.metadata.date": "Tanggal",
  "meeting.metadata.duration": "Durasi",
  "meeting.metadata.durationMinutes": { one: "{count} menit", other: "{count} menit" },
  "meeting.metadata.platform": "Platform",
  "meeting.metadata.participants": "Peserta",
  "meeting.metadata.participantCount": { one: "{count} peserta", other: "{count} peserta" },
  "meeting.activityViewDetails": "Lihat detail",
  "meeting.activityDetailsTitle": "Detail aktivitas",
  "meeting.activityDetailsSource": "Rapat",
  "meeting.activityDetailsTime": "Waktu",
  "meeting.segmentCount": { one: "{count} segmen", other: "{count} segmen" },
  "state.empty.title": "Belum ada apa pun",
  "state.error.title": "Terjadi kesalahan",
  "state.offline.description": "Periksa koneksi Anda. Informasi terakhir yang tersedia tetap ditampilkan.",
  "state.offline.title": "Anda sedang luring",
  "state.stale.description": "Informasi ini mungkin telah berubah. Segarkan sebelum mengambil tindakan.",
  "state.stale.title": "Pembaruan tersedia",
  "welcome.eyebrow": "Selamat datang",
  "welcome.title": "Rapat Anda, selalu teringat.",
  "welcome.intro": "Beri akses ke situs rapat yang Anda gunakan, lalu masuk sekali untuk menyatukan caption, transkrip, dan catatan Anda.",
  "welcome.point1": "Tangkap caption langsung dari Google Meet dan Zoom Web.",
  "welcome.point2": "Tinjau transkrip dan ringkasan yang dapat dicari di Memoru.",
  "welcome.point3": "Memoru hanya menangkap teks caption — tidak pernah audio rapat.",
  "welcome.setupTitle": "Siapkan ekstensi",
  "welcome.setupDescription": "Chrome akan meminta Anda mengonfirmasi akses sebelum ekstensi dapat berjalan di halaman rapat.",
  "welcome.continue": "Lanjut memberi akses",
  "welcome.missingDetails": "Detail penyiapan ekstensi tidak ditemukan. Buka halaman ini dari ekstensi Memoru lalu coba lagi.",
  "welcome.openError": "Tidak dapat membuka penyiapan ekstensi. Coba lagi.",
  "welcome.accessNote": "Anda dapat mengubah akses situs nanti di pengaturan ekstensi.",
  "connect.title": "Menghubungkan ekstensi Anda",
  "connect.description": "Mengonfirmasi sesi web Memoru Anda dengan Chrome. Cookie sesi web Anda tidak pernah dibagikan ke ekstensi.",
  "connect.missingDetails": "Detail penyiapan ekstensi tidak ditemukan. Kembali ke ekstensi dan mulai proses masuk lagi.",
  "connect.connecting": "Menghubungkan ekstensi",
  "connect.error": "Tidak dapat menghubungkan ekstensi. Coba lagi.",
  "auth.checkingSession": "Memeriksa sesi",
  "auth.checkingSetup": "Memeriksa penyiapan",
  "login.invalidLink": "Tautan masuk ini tidak valid atau sudah kedaluwarsa. Minta tautan ajaib baru.",
  "login.enterEmailReceived": "Masukkan alamat email yang menerima tautan ajaib.",
  "login.completeFailed": "Kami tidak dapat menyelesaikan proses masuk. Minta tautan ajaib baru atau coba lagi sebentar lagi.",
  "login.googleFailed": "Masuk dengan Google gagal. Coba lagi.",
  "login.invalidEmail": "Masukkan alamat email yang valid.",
  "login.sendFailed": "Tidak dapat mengirim email masuk saat ini. Coba lagi nanti.",
  "login.completeTitle": "Selesaikan proses masuk",
  "login.checkInbox": "Periksa kotak masuk Anda",
  "login.title": "Masuk ke Memoru",
  "login.confirmEmail": "Konfirmasi alamat email yang menerima tautan ajaib Anda.",
  "login.sentTo": "Kami mengirim tautan ajaib ke {email}. Buka untuk melanjutkan.",
  "login.tagline": "Catatan rapat Anda, ditangkap otomatis dari Google Meet dan Zoom.",
  "login.linkExpires": "Tautan kedaluwarsa dalam 15 menit. Anda dapat menutup jendela ini setelah membukanya.",
  "login.useAnother": "Gunakan email lain",
  "login.emailAddress": "Alamat email",
  "login.continue": "Lanjutkan",
  "login.sendLink": "Kirim tautan ajaib",
  "login.back": "Kembali",
  "login.google": "Lanjutkan dengan Google",
  "login.or": "atau",
  "login.email": "Lanjutkan dengan email",
  "login.unavailable": "Proses masuk sedang tidak tersedia. Coba lagi nanti.",
  "login.agreePrefix": "Dengan melanjutkan, Anda menyetujui ",
  "login.terms": "Ketentuan Layanan",
  "login.and": " dan ",
  "login.privacy": "Kebijakan Privasi",
  "login.agreeSuffix": " Memoru.",
  "login.err.rateLimit": "Terlalu banyak percobaan masuk. Coba lagi nanti.",
  "login.err.network": "Tidak dapat menjangkau layanan masuk. Periksa koneksi Anda lalu coba lagi.",
  "login.err.cancelled": "Masuk dengan Google dibatalkan.",
  "login.err.popupBlocked": "Izinkan pop-up untuk melanjutkan dengan Google.",
  "setup.eyebrow": "Penyiapan Memoru",
  "setup.checking": "Memeriksa penyiapan Anda",
  "setup.nameRequired": "Masukkan nama workspace.",
  "setup.createFailed": "Tidak dapat membuat workspace Anda. Coba lagi.",
  "setup.finishFailed": "Tidak dapat menyelesaikan penyiapan. Coba lagi.",
  "setup.createTitle": "Buat workspace Anda",
  "setup.createDescription": "Workspace menjaga rapat dan catatan Anda tertata sejak awal.",
  "setup.readyTitle": "Anda siap mengingat lebih banyak.",
  "setup.readyDescription": "Workspace Anda sudah siap. Buka rapat Google Meet atau Zoom Web dan Memoru akan hadir saat caption tersedia.",
  "setup.openMeetings": "Buka rapat saya",
  "profileName.title": "Siapa nama Anda?",
  "profileName.description": "Nama Anda tampil di rapat Anda dan bagi orang di workspace Anda. Anda dapat mengubahnya nanti di Pengaturan.",
  "profileName.label": "Nama lengkap",
  "profileName.placeholder": "mis. Budi Santoso",
  "profileName.hint": "Gunakan nama yang dikenal rekan tim Anda.",
  "profileName.required": "Masukkan nama Anda untuk melanjutkan.",
  "profileName.tooLong": "Gunakan maksimal {max} karakter.",
  "profileName.saveFailed": "Tidak dapat menyimpan nama Anda. Coba lagi.",
  "profileName.continue": "Lanjutkan",
  "profileName.signedInAs": "Masuk sebagai {email}",
  "profileName.switchAccount": "Gunakan akun lain",
  "settingsPage.title": "Pengaturan",
  "settingsPage.profile": "Profil",
  "settingsPage.sessions": "Sesi aktif",
  "settingsPage.signOutOthers": "Keluarkan sesi lain",
  "settingsPage.noSessions": "Tidak ada sesi aktif yang ditampilkan.",
  "settingsPage.current": "Saat ini",
  "settingsPage.revoke": "Cabut",
  "settingsPage.account": "Akun",
  "settingsPage.signOut": "Keluar",
  "settingsPage.signOutDescription": "Mengakhiri sesi Anda di perangkat ini.",
  "settingsPage.proPlan": "Paket Pro",
  "settingsPage.freePlan": "Paket Gratis",
  "settingsPage.defaultName": "Pengguna Memoru",
  "settingsPage.loadError": "Tidak dapat memuat sesi aktif.",
  "settingsPage.revokeError": "Tidak dapat mencabut sesi ini.",
  "settingsPage.revokeOthersError": "Tidak dapat mengeluarkan sesi lain.",
  "settingsPage.extension": "Ekstensi browser",
  "settingsPage.extensionChrome": "Ekstensi browser · Chrome",
  "settingsPage.safariIphone": "Safari di iPhone",
  "settingsPage.mobileBrowser": "Browser seluler",
  "settingsPage.browserOnMac": "{browser} di macOS",
  "settingsPage.browserOnWindows": "{browser} di Windows",
  "settingsPage.browser": "Browser",
  "settingsPage.legacy": "Klien lama",
  "settingsPage.web": "Browser web",
  "settingsPage.recentlyActive": "Aktif baru-baru ini",
  "settingsPage.activeNow": "Aktif sekarang",
  "settingsPage.emailNotifications": "Notifikasi email",
  "settingsPage.overviewEmail": "Ringkasan rapat siap",
  "settingsPage.overviewEmailOn": "Dapatkan email saat ringkasan rapat siap dibaca.",
  "settingsPage.overviewEmailOff": "Tidak ada email. Ringkasan Anda tetap ada di Rapat.",
  "settingsPage.emailFooter": "Dikirim ke {email}. Tautan masuk selalu dikirim.",
  "settingsPage.emailSaved": "Preferensi disimpan.",
  "settingsPage.emailSaveError": "Tidak dapat menyimpan preferensi email. Coba lagi.",
  "meetingStatus.live": "Langsung",
  "meetingStatus.ended": "Selesai",
  "overviewStatus.ready": "Siap",
  "overviewStatus.generating": "Sedang dibuat",
  "overviewStatus.failed": "Gagal",
  "overviewStatus.none": "Belum dibuat",
  "sort.newest": "Terbaru dulu",
  "sort.oldest": "Terlama dulu",
  "sort.segments": "Segmen terbanyak",
  "datePreset.any": "Kapan saja",
  "datePreset.today": "Hari ini",
  "datePreset.7d": "7 hari terakhir",
  "datePreset.30d": "30 hari terakhir",
  "datePreset.90d": "90 hari terakhir",
  "datePreset.custom": "Rentang kustom",
  "meetingList.loadingCount": "Memuat rapat…",
  "meetingList.countFiltered": "{count} dari {total} rapat",
  "meetingList.countMatching": "{count} rapat cocok",
  "meetingList.count": {"one": "{count} rapat", "other": "{count} rapat"},
  "meetingList.segments": {"one": "{count} segmen", "other": "{count} segmen"},
  "meetingList.minutes": "{count} mnt",
  "meetingList.startedAt": "Dimulai {time}",
  "meetingList.yesterday": "Kemarin, {time}",
  "meetingList.emptyDescription": "Ikuti panggilan di Google Meet atau Zoom dengan ekstensi Memoru terpasang, dan rapat akan muncul di sini secara otomatis.",
  "meetingList.getExtension": "Dapatkan ekstensi",
  "meetingList.howItWorks": "Cara kerjanya",
  "meetingList.howItWorksBody": "Ekstensi browser membaca caption selama rapat Anda. Memoru menyimpan transkrip di sini, lalu menyiapkan ringkasan setelah panggilan berakhir.",
  "meetingList.loadError": "Tidak dapat memuat rapat.",
  "meetingList.loadMoreError": "Tidak dapat memuat rapat lainnya.",
  "meetingList.searchPlaceholder": "Cari rapat…",
  "meetingList.appliedFilters": "Filter diterapkan",
  "meetingList.removeFilter": "Hapus filter {filter}",
  "meetingList.clearAll": "Hapus semua",
  "meetingList.unavailable": "Rapat tidak tersedia",
  "meetingList.noMatch": "Tidak ada rapat yang cocok dengan pencarian atau filter Anda.",
  "meetingList.noMatchHint": "Coba pencarian lain atau hapus filter.",
  "meetingList.loadMore": "Muat lebih banyak",
  "meetingList.loadingMore": "Memuat…",
  "filters.none": "Tidak ada filter dipilih",
  "filters.selected": {"one": "{count} filter dipilih", "other": "{count} filter dipilih"},
  "filters.close": "Tutup filter",
  "filters.clear": "Hapus",
  "filters.date": "Tanggal",
  "filters.platform": "Platform",
  "filters.status": "Status",
  "filters.overview": "Ikhtisar",
  "filters.invalidRange": "Tanggal mulai harus sama dengan atau sebelum tanggal akhir.",
  "filters.reset": "Atur ulang",
  "filters.show": "Tampilkan hasil",
  "filters.isAnyOf": "adalah salah satu dari",
  "filters.is": "adalah",
  "filters.platformsCount": "{count} platform",
  "filters.statusesCount": "{count} status",
  "detail.participants": "Peserta",
  "detail.waitingParticipants": "Menunggu peserta berbicara.",
  "detail.info": "Info rapat",
  "detail.started": "Dimulai",
  "detail.ended": "Berakhir",
  "detail.duration": "Durasi",
  "detail.segments": "Segmen",
  "detail.listening": "Mendengarkan percakapan…",
  "detail.listeningBody": "Segmen transkrip akan muncul di sini saat rapat berlangsung. Tab Ikhtisar terbuka setelah rapat ini berakhir.",
  "detail.unknownSpeaker": "Pembicara tidak dikenal",
  "detail.noTranscript": "Tidak ada transkrip yang ditangkap",
  "detail.noTranscriptBody": "Tidak ada segmen caption yang disimpan untuk rapat ini.",
  "detail.endTranscript": "— Akhir transkrip —",
  "detail.jump.missing": "Baris itu sudah tidak ada di transkrip.",
  "detail.loadingMoreTranscript": "Memuat transkrip lainnya…",
  "detail.loadingTranscript": "Memuat transkrip…",
  "detail.transcriptError": "Tidak dapat memuat transkrip.",
  "detail.search.label": "Cari transkrip",
  "detail.search.placeholder": "Cari di rapat ini, walau hanya sebagian kata",
  "detail.search.clear": "Hapus pencarian",
  "detail.search.previous": "Pencocokan sebelumnya",
  "detail.search.next": "Pencocokan berikutnya",
  "detail.search.position": "{index} dari {count}",
  "detail.search.searching": "Mencari…",
  "detail.search.noMatches": "Tidak ada yang cocok",
  "detail.search.noMatchesFor": "Tidak ada yang cocok untuk “{query}”.",
  "detail.search.noMatchesBody": "Coba kata yang lebih pendek atau ejaan lain.",
  "detail.search.error": "Tidak dapat mencari di transkrip.",
  "detail.search.matchTypes": "Jenis kecocokan",
  "detail.search.all": "Semua",
  "detail.search.exact": "Persis",
  "detail.search.similar": "Mirip",
  "detail.search.sounds": "Terdengar mirip",
  "detail.search.onlyMatching": "Hanya baris yang cocok",
  "detail.search.onlySpeaker": "Hanya {name}",
  "detail.search.showAllSpeakers": "Tampilkan semua pembicara",
  "detail.search.truncated": "Rapat ini panjang, jadi hanya bagian awalnya yang dicari.",
  "detail.search.moreMatches": "Menampilkan {count} pencocokan terbaik dari {total}.",
  "detail.view.open": "Opsi tampilan",
  "detail.view.chat": "Obrolan",
  "detail.view.chatBody": "Gelembung dengan avatar",
  "detail.view.lines": "Transkrip",
  "detail.view.linesBody": "Kolom waktu, seperti naskah",
  "detail.view.compact": "Ringkas",
  "detail.view.compactBody": "Baris padat, paling banyak teks per layar",
  "detail.view.merge": "Gabungkan pembicara yang sama",
  "detail.view.mergeBody": "Satu pesan untuk baris berurutan",
  "detail.view.timestamps": "Tampilkan waktu",
  "detail.view.timestampsBody": "Waktu di setiap pesan",
  "detail.view.colors": "Warna per pembicara",
  "detail.view.colorsBody": "Matikan untuk nama polos",
  "detail.view.reset": "Atur ulang tampilan per pesan",
  "detail.message.merge": "Gabung",
  "detail.message.toggle": "Ganti pesan ini antara gabungan dan segmen terpisah",
  "detail.message.chat": "Obrolan",
  "detail.message.matchAt": "Cocok pada {time} · lihat sebagai segmen",
  "detail.participants.total": "{count} peserta",
  "detail.participants.filter": "Hanya tampilkan {name}",
  "detail.noSpeakerMessages": "Tidak ada pesan dari pembicara ini.",
  "detail.loadingRecap": "Memuat ringkasan…",
  "detail.recapError": "Tidak dapat memuat ringkasan.",
  "detail.recapGenerating": "Sedang membuat ringkasan",
  "detail.recapGeneratingBody": "Ringkasan akan muncul di sini secara otomatis setelah siap.",
  "detail.recapFailed": "Pembuatan ringkasan gagal",
  "detail.recapFailedBody": "Ringkasan tidak dapat dibuat. Periksa kembali statusnya atau baca transkrip tersimpan.",
  "detail.recapSkipped": "Ringkasan tidak dibuat",
  "detail.recapSkippedBody": "Rapat ini tidak memenuhi syarat untuk diringkas. Anda tetap dapat membaca transkrip tersimpan.",
  "detail.noRecap": "Ringkasan belum tersedia",
  "detail.noRecapBody": "Ringkasan akan muncul setelah rapat berakhir dan pemrosesan selesai. Anda dapat membaca transkrip sambil menunggu.",
  "detail.readTranscript": "Baca transkrip",
  "detail.recap": "Ringkasan",
  "detail.generated": "Dibuat {when}",
  "detail.recently": "Baru saja",
  "detail.decisions": "Keputusan",
  "detail.aligned": "Disepakati",
  "detail.noDecisions": "Tidak ada keputusan yang dicatat.",
  "detail.nextSteps": "Langkah berikutnya",
  "detail.viewSource": "Lihat sumber",
  "detail.noNextSteps": "Tidak ada langkah berikutnya yang dicatat.",
  "detail.loadError": "Tidak dapat memuat rapat ini.",
  "detail.loadMoreError": "Tidak dapat memuat segmen transkrip lainnya.",
  "detail.back": "Kembali ke rapat",
  "titleMenu.renamed": "Nama rapat diubah",
  "titleMenu.renameError": "Tidak dapat mengubah nama rapat ini.",
  "titleMenu.deleteError": "Tidak dapat menghapus rapat ini.",
  "titleMenu.tooLong": "Judul rapat tidak boleh lebih dari {max} karakter ({count}/{max})",
  "titleMenu.hint": "Enter untuk simpan · Esc untuk batal",
  "titleMenu.deleteTitle": "Hapus rapat ini?",
  "titleMenu.deleteBody": "“{title}” akan dihapus dari {workspace} untuk semua orang, beserta transkrip dan ikhtisarnya.",
  "titleMenu.thisWorkspace": "workspace ini",
  "titleMenu.undo": "Tindakan ini tidak dapat dibatalkan.",
  "titleMenu.deleteConfirm": "Hapus rapat",
  "titleMenu.actionsFor": "Aksi rapat untuk {title}",
  "titleMenu.rename": "Ubah nama",
  "titleMenu.delete": "Hapus",
  "audio.player.label": "Audio rapat",
  "audio.player.play": "Putar",
  "audio.player.pause": "Jeda",
  "audio.player.speed": "Kecepatan putar",
  "audio.player.speedValue": "{rate}×",
  "audio.player.position": "Posisi audio",
  "audio.player.positionText": "{now} dari {length}",
  "audio.player.loadError": "Audio tidak dapat dimuat",
  "audio.line.play": "Putar dari {time}",
  "audio.line.noAudio": "Tanpa audio",
  "audio.divider.starts": "Audio dimulai pada {time} · baris sebelumnya tanpa audio",
  "audio.divider.notRecorded": "Tidak direkam {from}–{to}",
  "audio.divider.resumes": "Audio berlanjut pada {time}",
  "audio.transcribing.pending": "Transkrip dari audio sedang dibuat",
  "audio.toast.noAudioAt": "Tidak ada audio pada {time}. Audio dimulai pada {start}.",
  "audio.toast.playFrom": "Putar dari {start}",
  "audio.toast.skipping": "Melewati {duration} yang tidak direkam",
  "audio.toast.finishingUpload": "Menyelesaikan unggahan…",
  "audio.toast.retryFailed": "Transkrip belum bisa dibuat ulang. Coba lagi nanti.",
  "audio.alert.uploading.title": "Audio sedang diunggah",
  "audio.alert.uploading.progress": "Progres unggahan",
  "audio.alert.transcribing.title": "Mengubah audio menjadi transkrip",
  "audio.alert.transcribing.body": "Anda sudah bisa mendengarkannya. Transkrip akan muncul di sini setelah siap.",
  "audio.alert.failed.title": "Sebagian audio ini belum tersedia",
  "audio.alert.failed.body": "Buka memoru di komputer yang Anda pakai untuk merekam, dan kami akan menyelesaikan penyimpanannya.",
  "audio.alert.learnMore": "Pelajari lebih lanjut",
  "audio.alert.transcriptFailed.title": "Kami tidak dapat membuat transkrip",
  "audio.alert.transcriptFailed.body": "Audio Anda aman dan tetap bisa diputar. Coba lagi, atau kembali nanti.",
  "audio.alert.partial.title": "Rekaman ini tidak lengkap",
  "audio.alert.partial.body": "Audio berhenti pada {end} dari rapat berdurasi {length} menit, jadi transkrip dari audio ini juga berakhir di sana.",
  "audio.alert.partial.bodyAudioOnly": "Audio berhenti pada {end} dari rapat berdurasi {length} menit.",
  "audio.alert.deleted": "Audio ini sudah dihapus. Transkrip dan ringkasan tetap ada; waktu tidak lagi memutar audio.",
  "app.navigationSidebar": "Sidebar navigasi",
};

export const catalogs = { en, id } satisfies Record<Locale, typeof en>;
export type MessageKey = keyof typeof en;
